import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  CalendarDays,
  Camera,
  ChevronLeft,
  ChevronRight,
  Check,
  Edit,
  Eye,
  FileText,
  Film,
  GripVertical,
  Home,
  Image as ImageIcon,
  LayoutDashboard,
  LogIn,
  LogOut,
  MapPin,
  Plus,
  Search,
  Save,
  Settings,
  Share2,
  Shield,
  SlidersHorizontal,
  Trash2,
  Upload,
  Users,
  Waves,
  X
} from "lucide-react";
import type { Session } from "@supabase/supabase-js";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { demoEvents, demoMedia, demoPresentationSlides, demoSections } from "./lib/demoData";
import { isSupabaseConfigured, supabase } from "./lib/supabase";
import type { AllowedUser, EventInput, EventSection, FamilyEvent, MediaItem, PresentationSlide, Profile } from "./types";

const MEDIA_BUCKET = "family-event-media";
const ADMIN_EMAIL = "sige1203@gmail.com";
const EMPTY_PRESENTATION_PREVIEW_SLIDES: PresentationSlide[] = [];
type EventMode = "presentation" | "itinerary" | "memories";
type AppView = "public" | "admin" | "editor";
type ItineraryAction = {
  dayLabel: string;
  timeLabel: string;
  actionTitle: string;
  location: string;
  note: string;
};

type GeneratedPresentationSlide = {
  sectionKind: PresentationSlide["section_kind"];
  title: string;
  body: string;
  badge: string;
  imagePrompt: string;
  imageUrl: string;
  sortOrder: number;
  generationMeta: Record<string, unknown>;
};

const emptyEvent: EventInput = {
  title: "",
  slug: "",
  description: "",
  location: "",
  icon: "calendar",
  status: "draft",
  start_date: "",
  end_date: "",
  cover_image_url: "",
  is_published: false,
  sort_order: 100
};

const youtubeVideoId = (url: string) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) return parsed.pathname.split("/").filter(Boolean)[0] ?? "";
    if (parsed.pathname.startsWith("/embed/")) return parsed.pathname.split("/").filter(Boolean)[1] ?? "";
    if (parsed.pathname.startsWith("/shorts/")) return parsed.pathname.split("/").filter(Boolean)[1] ?? "";
    return parsed.searchParams.get("v") ?? "";
  } catch {
    const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
    return match?.[1] ?? "";
  }
};

const youtubeEmbedUrl = (url: string) => {
  const id = youtubeVideoId(url);
  return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1` : url;
};

const youtubeWatchUrl = (url: string) => {
  const id = youtubeVideoId(url);
  return id ? `https://www.youtube.com/watch?v=${id}` : url;
};

const isStorageImagePath = (item: MediaItem) =>
  item.type === "image" && Boolean(item.url) && !item.url.startsWith("http");

const isStoragePath = (url?: string | null) => Boolean(url) && !url!.startsWith("http") && !url!.startsWith("data:");

const fileBaseName = (name: string) => name.replace(/\.[^.]+$/, "") || "写真";

const formatDateRange = (event: FamilyEvent) => {
  if (!event.start_date) return "日程未定";
  if (!event.end_date || event.end_date === event.start_date) return event.start_date;
  return `${event.start_date} - ${event.end_date}`;
};

const actionButtons = [
  { label: "Presentation", sub: "概要・スライド", icon: Film, tone: "coral", mode: "presentation" },
  { label: "Itinerary", sub: "詳細・旅程表", icon: CalendarDays, tone: "teal", mode: "itinerary" },
  { label: "Memories", sub: "写真・動画アルバム", icon: Camera, tone: "sky", mode: "memories" }
] as const;

const isEventMode = (value?: string): value is EventMode =>
  value === "presentation" || value === "itinerary" || value === "memories";

const publicEventPath = (slug: string, mode: EventMode = "presentation") => `/events/${slug}/${mode}`;

const editorEventPath = (slug: string, mode: EventMode = "itinerary") => `/events/${slug}/edit/${mode}`;

const defaultDayLabels = ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5"];
const publicCheckItems = ["持ち物", "予約確認", "家族レビュー", "移動手段の確認", "天気・注意事項"];

const publicCheckStorageKey = (eventId: string) => `public-itinerary-checks:${eventId}`;
const publicOrderStorageKey = (eventId: string) => `public-itinerary-order:${eventId}`;

const readStringArrayStorage = (key: string) => {
  try {
    const value = window.localStorage.getItem(key);
    const parsed = value ? JSON.parse(value) : [];
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
};

const readOrderStorage = (key: string) => {
  try {
    const value = window.localStorage.getItem(key);
    const parsed = value ? JSON.parse(value) : {};
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).map(([day, ids]) => [
        day,
        Array.isArray(ids) ? ids.filter((item): item is string => typeof item === "string") : []
      ])
    );
  } catch {
    return {};
  }
};

const parseItineraryAction = (section: Pick<EventSection, "title" | "body">): ItineraryAction => {
  const titleMatch = section.title.match(/^(Day\s*\d+|出発前|帰宅後)\s+(?:(\d{1,2}:\d{2}|午前|午後|夕食|終日)\s+)?(.+)$/);
  const bodyLines = section.body.split("\n").map((line) => line.trim()).filter(Boolean);
  const locationLine = bodyLines.find((line) => line.startsWith("場所:") || line.startsWith("場所："));
  const location = locationLine?.replace(/^場所[:：]\s*/, "") ?? "";
  const note = bodyLines.filter((line) => line !== locationLine).join("\n");

  return {
    dayLabel: titleMatch?.[1] ?? "Day 1",
    timeLabel: titleMatch?.[2] ?? "",
    actionTitle: titleMatch?.[3] ?? section.title,
    location,
    note: note || section.body
  };
};

const buildItinerarySection = (action: ItineraryAction) => ({
  title: [action.dayLabel, action.timeLabel, action.actionTitle].filter(Boolean).join(" "),
  body: [action.location ? `場所: ${action.location}` : "", action.note].filter(Boolean).join("\n")
});

const sectionDayLabel = (section: EventSection) => parseItineraryAction(section).dayLabel;

const sectionTimeLabel = (section: EventSection, index: number) =>
  parseItineraryAction(section).timeLabel || `${String(9 + index).padStart(2, "0")}:00`;

const mediaActionPrefix = (label: string) => `旅程: ${label}`;

const splitMediaCaption = (caption?: string | null) => {
  if (!caption) return { actionLabel: "", caption: "" };
  const lines = caption.split("\n");
  const first = lines[0]?.trim() ?? "";
  if (!first.startsWith("旅程:") && !first.startsWith("旅程：")) {
    return { actionLabel: "", caption };
  }
  return {
    actionLabel: first.replace(/^旅程[:：]\s*/, ""),
    caption: lines.slice(1).join("\n")
  };
};

const buildMediaCaption = (caption: string, actionLabel: string) =>
  [actionLabel ? mediaActionPrefix(actionLabel) : "", caption.trim()].filter(Boolean).join("\n") || null;

const dataUrlToFile = (dataUrl: string, fileName: string) => {
  const [metadata, base64Data] = dataUrl.split(",");
  const mimeMatch = metadata.match(/^data:(.*?);base64$/);
  if (!mimeMatch || !base64Data) {
    throw new Error("Geminiから返された画像データURLが不正です。");
  }
  const binary = window.atob(base64Data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const extension = mimeMatch[1].split("/")[1] ?? "png";
  return new File([bytes], `${fileName}.${extension}`, { type: mimeMatch[1] });
};

const shouldKeepPresentationDataUrl = (slide: GeneratedPresentationSlide) =>
  slide.generationMeta?.imageFallback === true || slide.imageUrl.startsWith("data:image/svg+xml");

const generatedSlideToPresentationSlide = (
  slide: GeneratedPresentationSlide,
  eventId: string,
  index: number
): PresentationSlide => ({
  id: `preview-${eventId}-${index}`,
  event_id: eventId,
  section_kind: slide.sectionKind,
  title: slide.title,
  body: slide.body,
  badge: slide.badge || null,
  image_prompt: slide.imagePrompt || null,
  image_url: slide.imageUrl || null,
  sort_order: slide.sortOrder ?? index * 10,
  generation_metadata: slide.generationMeta ?? null
});

const presentationSlideToGeneratedSlide = (slide: PresentationSlide): GeneratedPresentationSlide => ({
  sectionKind: slide.section_kind,
  title: slide.title,
  body: slide.body,
  badge: slide.badge ?? "",
  imagePrompt: slide.image_prompt ?? "",
  imageUrl: slide.image_url ?? "",
  sortOrder: slide.sort_order,
  generationMeta: slide.generation_metadata ?? {}
});

const itineraryFallbackSlides = (
  event: FamilyEvent,
  eventSections: EventSection[],
  eventPhotos: MediaItem[]
): PresentationSlide[] => {
  const baseSlides: PresentationSlide[] = [
    {
      id: `${event.id}-fallback-cover`,
      event_id: event.id,
      section_kind: "cover",
      title: event.title,
      body: event.description,
      badge: "Family Trip",
      image_prompt: null,
      image_url: event.cover_image_url,
      sort_order: 0,
      generation_metadata: { source: "itinerary-fallback" }
    },
    ...eventSections.slice(0, 4).map((section, index): PresentationSlide => ({
      id: `${section.id}-fallback-presentation`,
      event_id: event.id,
      section_kind: "itinerary",
      title: index === 0 ? "旅の期待感" : section.title,
      body: section.body || event.description,
      badge: sectionDayLabel(section),
      image_prompt: null,
      image_url: eventPhotos[index]?.url ?? event.cover_image_url,
      sort_order: (index + 1) * 10,
      generation_metadata: { source: "itinerary-fallback" }
    }))
  ];

  return [
    ...baseSlides,
    {
      id: `${event.id}-fallback-summary`,
      event_id: event.id,
      section_kind: "summary",
      title: "まとめ",
      body: "家族で過ごす時間を、次に見返しやすい形で残します。",
      badge: "Summary",
      image_prompt: null,
      image_url: eventPhotos[baseSlides.length]?.url ?? event.cover_image_url,
      sort_order: 999,
      generation_metadata: { source: "itinerary-fallback" }
    }
  ];
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const uploadImageFile = async (eventId: string, file: File, prefix = "media") => {
  if (!supabase) throw new Error("Supabase is not configured.");
  const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const path = `${eventId}/${prefix}-${crypto.randomUUID()}.${extension}`;
  const uploadResult = await supabase.storage.from(MEDIA_BUCKET).upload(path, file, {
    cacheControl: "3600",
    contentType: file.type,
    upsert: false
  });
  if (uploadResult.error) throw uploadResult.error;
  return uploadResult.data.path;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState<FamilyEvent[]>([]);
  const [sections, setSections] = useState<EventSection[]>([]);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [presentationSlides, setPresentationSlides] = useState<PresentationSlide[]>([]);
  const [presentationPreviewSlides, setPresentationPreviewSlides] = useState<Record<string, PresentationSlide[]>>({});
  const [allowedUsers, setAllowedUsers] = useState<AllowedUser[]>([]);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const editorMatch = matchPath("/events/:slug/edit/:mode", location.pathname);
  const publicModeMatch = matchPath("/events/:slug/:mode", location.pathname);
  const publicSlugMatch = matchPath("/events/:slug", location.pathname);
  const publicDetailMatch = publicModeMatch ?? publicSlugMatch;
  const routeSlug = editorMatch?.params.slug ?? publicModeMatch?.params.slug ?? publicSlugMatch?.params.slug ?? null;
  const routeView: AppView = editorMatch ? "editor" : location.pathname.startsWith("/admin") ? "admin" : "public";
  const publicMode = isEventMode(publicModeMatch?.params.mode) ? publicModeMatch.params.mode : "presentation";
  const editorMode = isEventMode(editorMatch?.params.mode) ? editorMatch.params.mode : "itinerary";

  const selectedEvent = useMemo(() => {
    const first = events[0] ?? null;
    return events.find((event) => event.slug === routeSlug) ?? first;
  }, [events, routeSlug]);

  const admin = profile?.role === "admin";
  const needsLogin = isSupabaseConfigured && !session;
  const blockedUser =
    isSupabaseConfigured && Boolean(session) && Boolean(profile) && profile?.email.toLowerCase() !== ADMIN_EMAIL;

  useEffect(() => {
    const init = async () => {
      if (!supabase) {
        setEvents(demoEvents);
        setSections(demoSections);
        setMedia(demoMedia);
        setPresentationSlides(demoPresentationSlides);
        setLoading(false);
        return;
      }

      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      await loadPublicData();
      if (data.session) {
        await loadProfile(data.session.user.id);
      }
      setLoading(false);
    };

    init();

    if (!supabase) return undefined;
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (nextSession) {
        loadProfile(nextSession.user.id);
        loadPublicData();
      } else {
        setProfile(null);
        loadPublicData();
      }
    });

    return () => data.subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId: string) => {
    if (!supabase) return;
    const { data, error } = await supabase
      .from("profiles")
      .select("id,email,display_name,role")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      setMessage(`プロフィール取得に失敗しました: ${error.message}`);
      return;
    }
    setProfile(data);
    if (data?.role === "admin") {
      await loadAllowedUsers();
    } else {
      setAllowedUsers([]);
    }
  };

  const loadAllowedUsers = async () => {
    if (!supabase) return;
    const { data, error } = await supabase.from("allowed_users").select("email,role,created_at,updated_at").order("email");
    if (error) {
      setMessage(`許可メンバー取得に失敗しました: ${error.message}`);
      return;
    }
    setAllowedUsers(data ?? []);
  };

  const loadPublicData = async () => {
    if (!supabase) return;
    const [eventsResult, sectionsResult, mediaResult, presentationSlidesResult] = await Promise.all([
      supabase.from("events").select("*").order("sort_order").order("start_date", { ascending: true }),
      supabase.from("event_sections").select("*").order("sort_order"),
      supabase.from("media_items").select("*").order("sort_order"),
      supabase.from("presentation_slides").select("*").order("sort_order")
    ]);

    if (eventsResult.error || sectionsResult.error || mediaResult.error) {
      setMessage(
        eventsResult.error?.message ??
          sectionsResult.error?.message ??
          mediaResult.error?.message ??
          "データ取得に失敗しました"
      );
      return;
    }

    setEvents(eventsResult.data ?? []);
    setSections(sectionsResult.data ?? []);
    setMedia(mediaResult.data ?? []);
    if (presentationSlidesResult.error) {
      setPresentationSlides([]);
      setMessage("Presentation保存にはschema.sqlの適用が必要です。");
      return;
    }
    setPresentationSlides((presentationSlidesResult.data ?? []) as PresentationSlide[]);
  };

  if (loading) {
    return (
      <main className="app-shell">
        <div className="loading">読み込み中...</div>
      </main>
    );
  }

  const openPublic = (eventSlug?: string, mode?: EventMode) => {
    navigate(eventSlug ? publicEventPath(eventSlug, mode ?? "presentation") : "/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updatePresentationPreview = (eventId: string, slides: PresentationSlide[]) => {
    setPresentationPreviewSlides((current) => ({ ...current, [eventId]: slides }));
  };

  const openEditor = (eventSlug?: string, mode: EventMode = "itinerary") => {
    const slug = eventSlug ?? selectedEvent?.slug;
    if (!slug) return;
    navigate(editorEventPath(slug, mode));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showPublicChrome = true;

  return (
    <main className="app-shell">
      {showPublicChrome && (
        <header className="topbar">
          <button className="brand" type="button" onClick={() => openPublic()}>
            <span className="brand-mark">
              <Waves size={22} aria-hidden />
            </span>
            <span>
              <strong>Ikeda Family Events</strong>
              <small>Memories & Future Plans</small>
            </span>
          </button>
          <nav className="nav">
            <button className={routeView === "public" ? "active" : ""} type="button" onClick={() => openPublic()}>
              <Eye size={18} />
              公開
            </button>
            <button className={routeView === "admin" ? "active" : ""} type="button" onClick={() => navigate("/admin")}>
              <Shield size={18} />
              管理
            </button>
            <button className={routeView === "editor" ? "active" : ""} type="button" onClick={() => openEditor(selectedEvent?.slug, "itinerary")}>
              <Edit size={18} />
              編集
            </button>
          </nav>
        </header>
      )}

      {showPublicChrome && !isSupabaseConfigured && (
        <div className="notice">
          Supabase未設定のためデモデータで表示しています。`.env.local` を設定すると実データに切り替わります。
        </div>
      )}

      {message && (
        <div className="notice danger">
          {message}
          <button type="button" onClick={() => setMessage(null)}>
            閉じる
          </button>
        </div>
      )}

      {needsLogin ? (
        <LoginPanel
          title="家族ログイン"
          description="家族だけの予定と思い出を安全に管理します。"
          onMessage={setMessage}
        />
      ) : blockedUser ? (
        <AccessDeniedPanel email={profile?.email ?? session?.user.email ?? ""} />
      ) : routeView === "public" ? (
        <PublicView
          events={events.filter((event) => event.is_published || admin)}
          sections={sections}
          media={media}
          presentationSlides={presentationSlides}
          presentationPreviewSlides={presentationPreviewSlides}
          selectedEvent={selectedEvent}
          publicPage={publicDetailMatch ? "detail" : "list"}
          detailMode={publicMode}
          onOpenPublic={openPublic}
          onOpenEditor={openEditor}
          onMessage={setMessage}
        />
      ) : routeView === "admin" ? (
        <AdminView
          events={events}
          sections={sections}
          media={media}
          presentationSlides={presentationSlides}
          allowedUsers={allowedUsers}
          session={session}
          profile={profile}
          admin={admin}
          onOpenPublic={openPublic}
          onOpenEditor={openEditor}
          onRefresh={loadPublicData}
          onRefreshAllowedUsers={loadAllowedUsers}
          onMessage={setMessage}
        />
      ) : (
        <EditorView
          event={selectedEvent}
          events={events}
          sections={sections}
          media={media}
          presentationSlides={presentationSlides}
          presentationPreviewSlides={
            selectedEvent ? presentationPreviewSlides[selectedEvent.id] ?? EMPTY_PRESENTATION_PREVIEW_SLIDES : EMPTY_PRESENTATION_PREVIEW_SLIDES
          }
          onPresentationPreviewChange={updatePresentationPreview}
          session={session}
          profile={profile}
          mode={editorMode}
          onModeChange={(mode) => selectedEvent && navigate(editorEventPath(selectedEvent.slug, mode))}
          onBackToAdmin={() => navigate("/admin")}
          onOpenPublic={openPublic}
          onRefresh={loadPublicData}
          onMessage={setMessage}
        />
      )}
    </main>
  );
}

function AccessDeniedPanel({ email }: { email: string }) {
  return (
    <section className="empty-state access-denied">
      <Shield size={28} />
      <h1>このGoogleアカウントは許可されていません</h1>
      <p>
        現在のアカウント: <strong>{email || "不明"}</strong>
      </p>
      <p>
        このサイトは <strong>{ADMIN_EMAIL}</strong> のみログインできます。
      </p>
      <SignOutButton />
    </section>
  );
}

type PublicViewProps = {
  events: FamilyEvent[];
  sections: EventSection[];
  media: MediaItem[];
  presentationSlides: PresentationSlide[];
  presentationPreviewSlides: Record<string, PresentationSlide[]>;
  selectedEvent: FamilyEvent | null;
  publicPage: "list" | "detail";
  detailMode: EventMode;
  onOpenPublic: (eventSlug?: string, mode?: EventMode) => void;
  onOpenEditor: (eventSlug?: string, mode?: EventMode) => void;
  onMessage: (message: string | null) => void;
};

function PublicView({
  events,
  sections,
  media,
  presentationSlides,
  presentationPreviewSlides,
  selectedEvent,
  publicPage,
  detailMode,
  onOpenPublic,
  onOpenEditor,
  onMessage
}: PublicViewProps) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | FamilyEvent["status"]>("all");
  const [sortMode, setSortMode] = useState<"start-asc" | "start-desc" | "custom">("start-asc");
  const [memoryFilter, setMemoryFilter] = useState<"all" | MediaItem["type"]>("all");
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [lightbox, setLightbox] = useState<{ albumId: string; index: number } | null>(null);
  const [activeItineraryDay, setActiveItineraryDay] = useState<string | null>(null);
  const [checkedItineraryItems, setCheckedItineraryItems] = useState<string[]>([]);
  const [itineraryOrderByDay, setItineraryOrderByDay] = useState<Record<string, string[]>>({});
  const [draggingPublicRow, setDraggingPublicRow] = useState<{ id: string; day: string } | null>(null);
  const [publicDropTarget, setPublicDropTarget] = useState<{ id: string; position: "before" | "after" } | null>(null);

  useEffect(() => {
    setActiveItineraryDay(null);
  }, [selectedEvent?.id, detailMode]);

  useEffect(() => {
    if (!selectedEvent) return;
    setCheckedItineraryItems(readStringArrayStorage(publicCheckStorageKey(selectedEvent.id)));
    setItineraryOrderByDay(readOrderStorage(publicOrderStorageKey(selectedEvent.id)));
    setDraggingPublicRow(null);
    setPublicDropTarget(null);
  }, [selectedEvent?.id]);

  if (!selectedEvent) {
    return <EmptyState title="公開中のイベントがありません" body="管理画面からイベントを公開してください。" />;
  }

  const eventSections = sections.filter((section) => section.event_id === selectedEvent.id);
  const eventMedia = media.filter((item) => item.event_id === selectedEvent.id);
  const eventPhotos = eventMedia.filter((item) => item.type === "image");
  const videoCount = eventMedia.filter((item) => item.type === "youtube").length;
  const itineraryDays = Array.from(new Set(eventSections.map(sectionDayLabel)));
  const visibleItineraryDays = (itineraryDays.length ? itineraryDays : defaultDayLabels).slice(0, 5);
  const selectedItineraryDay = activeItineraryDay && visibleItineraryDays.includes(activeItineraryDay) ? activeItineraryDay : visibleItineraryDays[0];
  const itineraryGroups = (itineraryDays.length ? itineraryDays : defaultDayLabels)
    .map((day) => ({
      day,
      sections: eventSections
        .filter((section) => sectionDayLabel(section) === day)
        .sort((a, b) => {
          const order = itineraryOrderByDay[day] ?? [];
          const aIndex = order.indexOf(a.id);
          const bIndex = order.indexOf(b.id);
          if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
          if (aIndex >= 0) return -1;
          if (bIndex >= 0) return 1;
          return a.sort_order - b.sort_order;
        })
    }))
    .filter((group) => group.sections.length > 0);
  const checkedItineraryCount = publicCheckItems.filter((item) => checkedItineraryItems.includes(item)).length;
  const mapQuery = [selectedEvent.location, selectedEvent.title].filter(Boolean).join(" ") || selectedEvent.title;
  const mapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;
  const mapExternalUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;
  const previewPresentationSlides = selectedEvent ? presentationPreviewSlides[selectedEvent.id] ?? [] : [];
  const savedPresentationSlides = presentationSlides
    .filter((slide) => slide.event_id === selectedEvent.id)
    .sort((a, b) => a.sort_order - b.sort_order);
  const slides = previewPresentationSlides.length > 0
    ? previewPresentationSlides
    : savedPresentationSlides.length > 0
    ? savedPresentationSlides
    : itineraryFallbackSlides(selectedEvent, eventSections, eventPhotos);
  const selectedMediaPreview = eventMedia.find((item) => item.type === "youtube") ?? eventMedia[0];
  const remainingMedia = selectedMediaPreview
    ? eventMedia.filter((item) => item.id !== selectedMediaPreview.id)
    : eventMedia;
  const primaryPhotoCaption = splitMediaCaption(eventPhotos[0]?.caption);
  const memoryAlbums = Array.from(
    eventMedia
      .filter((item) => memoryFilter === "all" || item.type === memoryFilter)
      .reduce((albumMap, item) => {
        const captionInfo = splitMediaCaption(item.caption);
        const actionLabel = captionInfo.actionLabel || (item.type === "youtube" ? "紹介動画" : "未分類の写真");
        const section = eventSections.find((candidate) => candidate.title === actionLabel);
        const album = albumMap.get(actionLabel) ?? {
          id: actionLabel,
          title: section ? parseItineraryAction(section).actionTitle : actionLabel,
          dayLabel: section ? sectionDayLabel(section) : "",
          timeLabel: section ? sectionTimeLabel(section, 0) : "",
          location: section ? parseItineraryAction(section).location : "",
          note: captionInfo.caption || "",
          items: [] as MediaItem[],
          firstSort: item.sort_order
        };
        album.items.push(item);
        album.firstSort = Math.min(album.firstSort, item.sort_order);
        albumMap.set(actionLabel, album);
        return albumMap;
      }, new Map<string, { id: string; title: string; dayLabel: string; timeLabel: string; location: string; note: string; items: MediaItem[]; firstSort: number }>())
      .values()
  ).sort((a, b) => a.firstSort - b.firstSort);
  const lightboxAlbum = lightbox ? memoryAlbums.find((album) => album.id === lightbox.albumId) : null;
  const lightboxItems = lightboxAlbum?.items ?? [];
  const lightboxIndex = lightboxItems.length > 0 && lightbox ? (lightbox.index + lightboxItems.length) % lightboxItems.length : 0;
  const lightboxItem = lightboxItems[lightboxIndex] ?? null;
  const visibleEvents = [...events]
    .filter((event) => {
      const normalizedQuery = query.trim().toLowerCase();
      const matchesQuery =
        !normalizedQuery ||
        [event.title, event.description, event.location ?? ""].some((value) =>
          value.toLowerCase().includes(normalizedQuery)
        );
      const matchesStatus = statusFilter === "all" || event.status === statusFilter;
      return matchesQuery && matchesStatus;
    })
    .sort((a, b) => {
      if (sortMode === "custom") return a.sort_order - b.sort_order;
      const aDate = a.start_date ?? "9999-12-31";
      const bDate = b.start_date ?? "9999-12-31";
      return sortMode === "start-asc" ? aDate.localeCompare(bDate) : bDate.localeCompare(aDate);
    });

  const shareEvent = async () => {
    const url = `${window.location.origin}${publicEventPath(selectedEvent.slug, detailMode)}`;
    try {
      await navigator.clipboard?.writeText(url);
      onMessage("共有リンクをコピーしました。");
    } catch {
      onMessage(`共有リンク: ${url}`);
    }
  };

  const closePresentationPreview = () => {
    if (detailMode === "presentation" && previewPresentationSlides.length > 0) {
      onOpenEditor(selectedEvent.slug, "presentation");
      return;
    }
    onOpenPublic();
  };

  const scrollToItineraryDay = (day: string) => {
    setActiveItineraryDay(day);
    document.getElementById(`itinerary-${slugify(day)}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openAlbumLightbox = (albumId: string, index: number) => {
    setLightbox({ albumId, index });
  };

  const toggleItineraryCheck = (item: string) => {
    setCheckedItineraryItems((current) => {
      const next = current.includes(item) ? current.filter((value) => value !== item) : [...current, item];
      window.localStorage.setItem(publicCheckStorageKey(selectedEvent.id), JSON.stringify(next));
      return next;
    });
  };

  const reorderPublicItineraryRow = (targetSectionId: string, targetDay: string, position: "before" | "after") => {
    if (!draggingPublicRow || draggingPublicRow.id === targetSectionId || draggingPublicRow.day !== targetDay) {
      setDraggingPublicRow(null);
      setPublicDropTarget(null);
      return;
    }

    const groupSections = itineraryGroups.find((group) => group.day === targetDay)?.sections ?? [];
    const orderedIds = groupSections.map((section) => section.id);
    const fromIndex = orderedIds.indexOf(draggingPublicRow.id);
    const targetIndex = orderedIds.indexOf(targetSectionId);
    if (fromIndex < 0 || targetIndex < 0) {
      setDraggingPublicRow(null);
      setPublicDropTarget(null);
      return;
    }

    const nextIds = [...orderedIds];
    const [movedId] = nextIds.splice(fromIndex, 1);
    const adjustedTargetIndex = nextIds.indexOf(targetSectionId);
    nextIds.splice(position === "after" ? adjustedTargetIndex + 1 : adjustedTargetIndex, 0, movedId);
    const nextOrder = { ...itineraryOrderByDay, [targetDay]: nextIds };
    setItineraryOrderByDay(nextOrder);
    window.localStorage.setItem(publicOrderStorageKey(selectedEvent.id), JSON.stringify(nextOrder));
    setDraggingPublicRow(null);
    setPublicDropTarget(null);
  };

  const moveLightbox = (direction: -1 | 1) => {
    setLightbox((current) => {
      if (!current) return current;
      const album = memoryAlbums.find((item) => item.id === current.albumId);
      const items = album?.items ?? [];
      if (items.length === 0) return current;
      return { ...current, index: (current.index + direction + items.length) % items.length };
    });
  };

  useEffect(() => {
    const enabled = publicPage === "detail" && detailMode === "presentation";
    document.body.classList.toggle("presentation-fullscreen", enabled);
    return () => document.body.classList.remove("presentation-fullscreen");
  }, [detailMode, publicPage]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLightbox(null);
        return;
      }
      if (event.key === "ArrowLeft") {
        moveLightbox(-1);
        return;
      }
      if (event.key === "ArrowRight") {
        moveLightbox(1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("lightbox-open");
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("lightbox-open");
    };
  }, [lightbox, memoryAlbums]);

  if (publicPage === "detail") {
    if (detailMode === "presentation") {
      return (
        <div className="presentation-stage">
          <header className="presentation-stage-topbar">
            <button className="presentation-stage-brand" type="button" onClick={() => onOpenPublic()}>
              <span className="brand-mark compact">
                <Waves size={26} />
              </span>
              <strong>Ikeda Family Events</strong>
            </button>
            <nav className="presentation-stage-tabs" aria-label={`${selectedEvent.title}の公開ページ切替`}>
              {actionButtons.map(({ label, mode }) => (
                <button
                  className={detailMode === mode ? "active" : ""}
                  key={mode}
                  type="button"
                  onClick={() => onOpenPublic(selectedEvent.slug, mode)}
                >
                  {label}
                </button>
              ))}
            </nav>
            <div className="presentation-stage-actions">
              <button type="button" onClick={shareEvent}>
                <Share2 size={18} />
                共有
              </button>
              <button className="icon-only" type="button" onClick={closePresentationPreview} aria-label="閉じる">
                <X size={24} />
              </button>
            </div>
          </header>

          <main className="presentation-story">
            {slides.map((slide, index) => (
              <section className={`presentation-story-slide ${slide.section_kind}`} key={slide.id}>
                <CoverImage src={slide.image_url} alt="" />
                <div className="presentation-slide-scrim" />
                <div className="presentation-story-copy">
                  <span>{slide.badge ?? slide.section_kind}</span>
                  <h1>{index === 0 ? selectedEvent.title : slide.title}</h1>
                  <p>{slide.body}</p>
                  <small>{index + 1} / {slides.length}</small>
                </div>
              </section>
            ))}
            <section className="presentation-story-end">
              <span>
                <Camera size={17} />
                写真 {eventPhotos.length}
              </span>
              <span>
                <Film size={17} />
                動画 {videoCount}
              </span>
              <button type="button" onClick={() => onOpenPublic(selectedEvent.slug, "itinerary")}>
                Itineraryを見る
              </button>
            </section>
          </main>
        </div>
      );
    }

    return (
      <div className="detail-page">
        <div className="detail-nav">
          <button type="button" onClick={() => onOpenPublic()}>
            ← イベント一覧
          </button>
          <span>›</span>
          <strong>{selectedEvent.title}</strong>
          <div className="detail-actions">
            <button type="button" onClick={() => onOpenEditor(selectedEvent.slug, detailMode)}>
              <Edit size={17} />
              編集
            </button>
            <button type="button" onClick={shareEvent}>
              <Share2 size={17} />
              共有
            </button>
            <button className="danger-action" type="button" onClick={() => onOpenEditor(selectedEvent.slug, "memories")}>
              <Plus size={17} />
              アルバムに追加
            </button>
          </div>
        </div>

        <article className="event-detail exact-detail">
          <section className="detail-hero">
            <CoverImage src={selectedEvent.cover_image_url} alt="" />
            <div className="detail-hero-copy">
              <h1>{selectedEvent.title}</h1>
              <div className="meta-row">
                <span>
                  <CalendarDays size={18} />
                  {formatDateRange(selectedEvent)}
                </span>
                {selectedEvent.location && (
                  <span>
                    <MapPin size={18} />
                    {selectedEvent.location}
                  </span>
                )}
              </div>
            </div>
          </section>

          <div className="detail-mode-tabs" role="tablist" aria-label={`${selectedEvent.title}の表示切替`}>
            {actionButtons.map(({ label, icon: Icon, mode }) => (
              <button
                className={detailMode === mode ? "detail-mode-tab active" : "detail-mode-tab"}
                key={mode}
                type="button"
                onClick={() => onOpenPublic(selectedEvent.slug, mode)}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>

          {detailMode === "itinerary" && (
            <section className="public-itinerary">
              <div className="public-itinerary-main">
                <div className="public-section-kicker">
                  <CalendarDays size={22} />
                  <div>
                    <h2>Itinerary</h2>
                    <span>旅程と確認事項</span>
                  </div>
                </div>
                <div className="public-day-tabs">
                  {visibleItineraryDays.map((day) => (
                    <button
                      className={day === selectedItineraryDay ? "active" : ""}
                      type="button"
                      key={day}
                      onClick={() => scrollToItineraryDay(day)}
                    >
                      {day}
                    </button>
                  ))}
                </div>
                <div className="public-itinerary-days">
                  {itineraryGroups.map((group) => (
                    <section className="public-itinerary-day" id={`itinerary-${slugify(group.day)}`} key={group.day}>
                      <div className="public-day-heading">
                        <span>{group.day}</span>
                        <strong>{group.sections.length}件の予定</strong>
                      </div>
                      <div className="public-timeline">
                        {group.sections.map((section, index) => {
                          const action = parseItineraryAction(section);
                          const dropPosition = publicDropTarget?.id === section.id ? publicDropTarget.position : null;
                          return (
                            <article
                              className={[
                                "public-itinerary-row",
                                draggingPublicRow?.id === section.id ? "dragging" : "",
                                dropPosition ? `drop-${dropPosition}` : ""
                              ].filter(Boolean).join(" ")}
                              draggable
                              key={section.id}
                              onDragStart={(event) => {
                                setDraggingPublicRow({ id: section.id, day: group.day });
                                event.dataTransfer.effectAllowed = "move";
                                event.dataTransfer.setData("text/plain", section.id);
                              }}
                              onDragEnd={() => {
                                setDraggingPublicRow(null);
                                setPublicDropTarget(null);
                              }}
                              onDragOver={(event) => {
                                if (!draggingPublicRow || draggingPublicRow.day !== group.day) return;
                                event.preventDefault();
                                const rect = event.currentTarget.getBoundingClientRect();
                                const position = event.clientY - rect.top > rect.height / 2 ? "after" : "before";
                                setPublicDropTarget({ id: section.id, position });
                              }}
                              onDrop={(event) => {
                                event.preventDefault();
                                reorderPublicItineraryRow(section.id, group.day, publicDropTarget?.position ?? "after");
                              }}
                            >
                              <div className="public-time">
                                <GripVertical size={18} />
                                <strong>{sectionTimeLabel(section, index)}</strong>
                              </div>
                              <div className="public-line-node" />
                              <div className="public-itinerary-card">
                                <CalendarDays size={22} />
                                <div>
                                  <h3>{action.actionTitle}</h3>
                                  {action.location && <small>{action.location}</small>}
                                  <p>{action.note}</p>
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
                <button className="download-row" type="button">
                  <FileText size={17} />
                  旅程表（PDF）をダウンロード
                </button>
              </div>
              <aside className="public-itinerary-aside">
                <div className="public-check-panel">
                  <div>
                    <h3>旅程確認</h3>
                    <strong>{checkedItineraryCount} / {publicCheckItems.length}</strong>
                  </div>
                  {publicCheckItems.map((item) => {
                    const checked = checkedItineraryItems.includes(item);
                    return (
                      <button
                        className={checked ? "checked" : ""}
                        type="button"
                        key={item}
                        onClick={() => toggleItineraryCheck(item)}
                        aria-pressed={checked}
                      >
                        <span>{item}</span>
                        <span className={checked ? "status-chip done" : "status-chip"}>{checked ? "確認済み" : "未確認"}</span>
                        <Check size={16} />
                      </button>
                    );
                  })}
                </div>
                <div className="public-map-panel">
                  <h3>エリアマップ</h3>
                  <iframe
                    className="public-map-frame"
                    title={`${selectedEvent.title}のGoogleマップ`}
                    src={mapEmbedUrl}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <a className="public-map-link" href={mapExternalUrl} target="_blank" rel="noreferrer">
                    <MapPin size={16} />
                    Google Mapsで開く
                  </a>
                </div>
                <div className="public-next-panel">
                  <Shield size={30} />
                  <div>
                    <h3>最終確認ステータス</h3>
                    <p>出発前にすべての項目を確認して、家族で共有しましょう。</p>
                  </div>
                  <button type="button" onClick={shareEvent}>家族へ共有</button>
                </div>
              </aside>
            </section>
          )}

          {detailMode === "memories" && (
            <section className="public-memories">
              <div className="public-section-kicker">
                <ImageIcon size={22} />
                <div>
                  <h2>Memories</h2>
                  <span>写真・動画アルバム</span>
                </div>
              </div>
              <div className="memories-toolbar">
                <div>
                  <button className={memoryFilter === "all" ? "active" : ""} type="button" onClick={() => setMemoryFilter("all")}>
                    すべて
                  </button>
                  <button className={memoryFilter === "image" ? "active" : ""} type="button" onClick={() => setMemoryFilter("image")}>
                    写真
                  </button>
                  <button className={memoryFilter === "youtube" ? "active" : ""} type="button" onClick={() => setMemoryFilter("youtube")}>
                    動画
                  </button>
                </div>
                <button type="button" onClick={() => setPhotoModalOpen(true)} disabled={eventPhotos.length === 0}>
                  すべての写真を見る
                </button>
              </div>
              <div className="memory-album-summary">
                <div>
                  <strong>{memoryAlbums.length}</strong>
                  <span>アルバム</span>
                </div>
                <div>
                  <strong>{eventPhotos.length}</strong>
                  <span>写真</span>
                </div>
                <div>
                  <strong>{videoCount}</strong>
                  <span>動画</span>
                </div>
                <p>{primaryPhotoCaption.caption || "行動ごとに写真と動画をまとめ、旅の流れに沿って見返せるアルバムです。"}</p>
              </div>
              <div className="memory-timeline">
                {memoryAlbums.map((album) => {
                  const imageItems = album.items.filter((item) => item.type === "image");
                  const videoItems = album.items.filter((item) => item.type === "youtube");
                  return (
                    <article className="memory-timeline-item" key={album.id}>
                      <div className="memory-timeline-marker">
                        <span>{album.dayLabel || "Album"}</span>
                      </div>
                      <div className="memory-timeline-content">
                        <div className="memory-timeline-head">
                          <div>
                            <div className="memory-timeline-chips">
                              {album.timeLabel && <span className="memory-action-chip">{album.timeLabel}</span>}
                              {album.location && <span className="memory-action-chip">{album.location}</span>}
                            </div>
                            <h3>{album.title}</h3>
                          </div>
                          <div className="memory-album-meta">
                            <span>
                              <ImageIcon size={16} />
                              {imageItems.length}
                            </span>
                            <span>
                              <Film size={16} />
                              {videoItems.length}
                            </span>
                          </div>
                        </div>
                        <p>{album.note || "写真と動画をこの行動のアルバムとしてまとめています。"}</p>
                        {album.items.length > 0 && (
                          <div className="memory-timeline-media">
                            {album.items.map((item, index) => (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => openAlbumLightbox(album.id, index)}
                                aria-label={`${album.title}の${item.type === "image" ? "写真" : "動画"}${index + 1}を開く`}
                              >
                                {item.type === "image" ? (
                                  <CoverImage src={item.url} alt="" />
                                ) : (
                                  <span className="memory-strip-video-thumb">
                                    <Film size={20} />
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
                {memoryAlbums.length === 0 && (
                  <div className="memory-empty-panel">
                    <ImageIcon size={34} />
                    <strong>アルバムがありません</strong>
                    <span>写真または動画を追加すると、行動ごとのアルバムとして表示されます。</span>
                  </div>
                )}
              </div>
            </section>
          )}
        </article>
        {photoModalOpen && (
          <div className="modal-backdrop" role="presentation">
            <section className="photo-modal" role="dialog" aria-modal="true" aria-label={`${selectedEvent.title}の写真一覧`}>
              <div className="photo-modal-head">
                <div>
                  <h2>写真一覧</h2>
                  <p>{selectedEvent.title}</p>
                </div>
                <button type="button" onClick={() => setPhotoModalOpen(false)}>
                  閉じる
                </button>
              </div>
              <div className="photo-modal-grid">
                {eventPhotos.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          </div>
        )}
        {lightbox && lightboxItem && lightboxAlbum && (
          <div className="memory-lightbox" role="dialog" aria-modal="true" aria-label={`${lightboxAlbum.title}のメディアビューア`}>
            <button className="memory-lightbox-close" type="button" onClick={() => setLightbox(null)} aria-label="メディアビューアを閉じる">
              <X size={28} />
            </button>
            <button className="memory-lightbox-nav left" type="button" onClick={() => moveLightbox(-1)} aria-label="前のメディア">
              <ChevronLeft size={36} />
            </button>
            <figure className="memory-lightbox-stage">
              {lightboxItem.type === "image" ? (
                <CoverImage src={lightboxItem.url} alt="" />
              ) : (
                <div className="memory-lightbox-video">
                  <iframe
                    title={lightboxItem.title}
                    src={youtubeEmbedUrl(lightboxItem.url)}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                  <a className="memory-lightbox-youtube-link" href={youtubeWatchUrl(lightboxItem.url)} target="_blank" rel="noreferrer">
                    YouTubeで再生
                  </a>
                </div>
              )}
              <figcaption>
                <span>{lightboxAlbum.dayLabel || "Album"} {lightboxAlbum.timeLabel}</span>
                <strong>{lightboxItem.title || lightboxAlbum.title}</strong>
                <small>{lightboxIndex + 1} / {lightboxItems.length}</small>
              </figcaption>
            </figure>
            <button className="memory-lightbox-nav right" type="button" onClick={() => moveLightbox(1)} aria-label="次のメディア">
              <ChevronRight size={36} />
            </button>
            <div className="memory-lightbox-strip">
              {lightboxItems.map((item, index) => (
                <button
                  className={index === lightboxIndex ? "active" : ""}
                  key={item.id}
                  type="button"
                  onClick={() => setLightbox({ albumId: lightboxAlbum.id, index })}
                  aria-label={`${index + 1}件目へ移動`}
                >
                  {item.type === "image" ? (
                    <CoverImage src={item.url} alt="" />
                  ) : (
                    <span className="memory-lightbox-video-thumb">
                      <Film size={22} />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="event-list-page">
      <section className="event-list-hero">
        <div>
          <div className="hero-title-row">
            <CalendarDays size={42} />
            <h1>家族イベント一覧</h1>
          </div>
          <p>これまでの思い出と、これからの楽しみをまとめています。</p>
        </div>
        <div className="island-line" />
      </section>

      <div className="event-list-layout">
        <aside className="filter-sidebar">
          <label>
            検索
            <span className="search-box">
              <Search size={17} />
              <input
                placeholder="イベント名で検索"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </span>
          </label>
          <label>
            ステータス
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}>
              <option value="all">すべて</option>
              <option value="upcoming">UPCOMING</option>
              <option value="completed">COMPLETED</option>
              <option value="draft">DRAFT</option>
            </select>
          </label>
          <label>
            並び順
            <select value={sortMode} onChange={(event) => setSortMode(event.target.value as typeof sortMode)}>
              <option value="start-asc">開始日が近い順</option>
              <option value="start-desc">開始日が遠い順</option>
              <option value="custom">管理画面の表示順</option>
            </select>
          </label>
        </aside>

        <section className="event-list-main">
          <div className="list-toolbar">
            <strong>{visibleEvents.length} 件のイベント</strong>
          </div>

          <div className="event-card-grid">
            {visibleEvents.map((event) => (
              <article className="event-card" key={event.id}>
                <div className="event-image-wrap">
                  <CoverImage src={event.cover_image_url} alt="" />
                  <span className="status floating">{event.status}</span>
                </div>
                <div className="event-card-body">
                  <h2>{event.title}</h2>
                  <div className="date-line">
                    <CalendarDays size={18} />
                    {formatDateRange(event)}
                  </div>
                  <p>{event.description}</p>
                  <div className="card-actions">
                    {actionButtons.map(({ label, sub, icon: Icon, tone, mode }) => (
                      <button
                        className={`action-button ${tone}`}
                        key={label}
                        type="button"
                        onClick={() => onOpenPublic(event.slug, mode)}
                      >
                        <Icon size={18} />
                        <span>
                          <strong>{label}</strong>
                          <small>{sub}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </article>
            ))}
            {visibleEvents.length === 0 && (
              <article className="new-event-card">
                <div className="new-event-icon">
                  <Search size={42} />
                </div>
                <strong>No Events</strong>
                <span>条件に一致するイベントはありません。</span>
              </article>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function MediaCard({ item }: { item: MediaItem }) {
  const [imageUrl, setImageUrl] = useState(item.url);
  const captionInfo = splitMediaCaption(item.caption);

  useEffect(() => {
    const createImageUrl = async () => {
      if (item.type !== "image" || item.url.startsWith("http") || !supabase) {
        setImageUrl(item.url);
        return;
      }

      const { data, error } = await supabase.storage.from(MEDIA_BUCKET).createSignedUrl(item.url, 60 * 60);
      setImageUrl(error ? "" : data.signedUrl);
    };

    createImageUrl();
  }, [item.type, item.url]);

  if (item.type === "youtube") {
    return (
      <div className="media-card">
        <iframe
          title={item.title}
          src={youtubeEmbedUrl(item.url)}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
        <div>
          <strong>{item.title}</strong>
          {captionInfo.actionLabel && <span className="memory-action-chip">{captionInfo.actionLabel}</span>}
          {captionInfo.caption && <p>{captionInfo.caption}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="media-card image-card">
      {imageUrl ? (
        <img src={imageUrl} alt={item.title} loading="lazy" />
      ) : (
        <div className="image-placeholder">画像を表示できません</div>
      )}
      <div>
        <strong>{item.title}</strong>
        {captionInfo.actionLabel && <span className="memory-action-chip">{captionInfo.actionLabel}</span>}
        {captionInfo.caption && <p>{captionInfo.caption}</p>}
      </div>
    </div>
  );
}

function CoverImage({ src, alt }: { src: string | null; alt: string }) {
  const [imageUrl, setImageUrl] = useState(src ?? "");

  useEffect(() => {
    const createImageUrl = async () => {
      if (!src) {
        setImageUrl("");
        return;
      }
      if (!isStoragePath(src) || !supabase) {
        setImageUrl(src);
        return;
      }

      const { data, error } = await supabase.storage.from(MEDIA_BUCKET).createSignedUrl(src, 60 * 60);
      setImageUrl(error ? "" : data.signedUrl);
    };

    createImageUrl();
  }, [src]);

  if (!imageUrl) return null;
  return <img src={imageUrl} alt={alt} loading="lazy" />;
}

type AdminViewProps = {
  events: FamilyEvent[];
  sections: EventSection[];
  media: MediaItem[];
  presentationSlides: PresentationSlide[];
  allowedUsers: AllowedUser[];
  session: Session | null;
  profile: Profile | null;
  admin: boolean;
  onOpenPublic: (eventSlug?: string, mode?: EventMode) => void;
  onOpenEditor: (eventSlug?: string, mode?: EventMode) => void;
  onRefresh: () => Promise<void>;
  onRefreshAllowedUsers: () => Promise<void>;
  onMessage: (message: string | null) => void;
};

function AdminView({
  events,
  sections,
  media,
  presentationSlides,
  allowedUsers,
  session,
  profile,
  admin,
  onOpenPublic,
  onOpenEditor,
  onRefresh,
  onRefreshAllowedUsers,
  onMessage
}: AdminViewProps) {
  const canManage = admin || !isSupabaseConfigured;
  const userEmail = session?.user.email ?? "your-email@example.com";
  const [activeAdminSection, setActiveAdminSection] = useState<"events" | "members" | "settings">("events");
  const [selectedAdminEventId, setSelectedAdminEventId] = useState(events[0]?.id ?? "");
  const eventsRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const resourceStats = [
    {
      label: "イベント",
      value: events.length,
      limit: 100,
      detail: `${events.filter((event) => event.is_published).length} 公開中`
    },
    {
      label: "旅程セクション",
      value: sections.length,
      limit: 1000,
      detail: `${new Set(sections.map((section) => section.event_id)).size} イベントに紐づけ`
    },
    {
      label: "メディア",
      value: media.length,
      limit: 5000,
      detail: `${media.filter((item) => item.type === "image").length} 写真 / ${media.filter((item) => item.type === "youtube").length} 動画`
    },
    {
      label: "メンバー",
      value: allowedUsers.length,
      limit: 20,
      detail: `${allowedUsers.filter((user) => user.role === "admin").length} 管理者`
    }
  ];

  useEffect(() => {
    if (!selectedAdminEventId && events[0]) {
      setSelectedAdminEventId(events[0].id);
      return;
    }
    if (selectedAdminEventId && !events.some((event) => event.id === selectedAdminEventId)) {
      setSelectedAdminEventId(events[0]?.id ?? "");
    }
  }, [events, selectedAdminEventId]);

  const scrollToAdminSection = (
    section: "events" | "members" | "settings",
    ref: React.RefObject<HTMLDivElement | null>
  ) => {
    setActiveAdminSection(section);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openEventEditor = (eventId: string, mode: EventMode = "itinerary") => {
    const event = events.find((item) => item.id === eventId);
    setSelectedAdminEventId(eventId);
    onOpenEditor(event?.slug, mode);
  };

  if (isSupabaseConfigured && !session) {
    return <LoginPanel title="管理者ログイン" onMessage={onMessage} />;
  }

  if (!canManage) {
    return (
      <section className="admin-card permission-card">
        <div className="admin-heading">
          <Shield size={24} />
          <div>
            <h1>管理権限がありません</h1>
            <p>{profile?.email ?? userEmail} はログイン済みですが、adminではありません。</p>
          </div>
        </div>
        <pre className="sql-hint">
{`update public.profiles
set role = 'admin'
where email = '${userEmail}';`}
        </pre>
        <SignOutButton />
      </section>
    );
  }

  return (
    <section className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <Waves size={30} />
          <strong>管理画面</strong>
        </div>
        <button
          className={activeAdminSection === "events" ? "sidebar-item active" : "sidebar-item"}
          type="button"
          onClick={() => scrollToAdminSection("events", eventsRef)}
        >
          <LayoutDashboard size={18} />
          イベント一覧
        </button>
        <button
          className={activeAdminSection === "members" ? "sidebar-item active" : "sidebar-item"}
          type="button"
          onClick={() => scrollToAdminSection("members", membersRef)}
        >
          <Shield size={18} />
          メンバー
        </button>
        <button
          className={activeAdminSection === "settings" ? "sidebar-item active" : "sidebar-item"}
          type="button"
          onClick={() => scrollToAdminSection("settings", settingsRef)}
        >
          <SlidersHorizontal size={18} />
          設定
        </button>
        <div className="admin-user-card">
          <Shield size={24} />
          <span>管理者としてログイン中</span>
          <strong>{profile?.email ?? "demo@example.com"}</strong>
          <SignOutButton />
        </div>
      </aside>

      <div className="admin-main">
        <div className="admin-topline">
          <div>
            <h1>管理画面</h1>
            <p>イベントの作成、公開管理、メンバー設定を行います。</p>
          </div>
          <div className="admin-toolbar">
            {!isSupabaseConfigured && <span className="demo-pill">Demo mode</span>}
            <Bell size={22} />
            <button type="button" onClick={() => onOpenPublic()}>
              公開
            </button>
          </div>
        </div>

        <div className="admin-card admin-section-card" ref={eventsRef}>
          <div className="admin-heading">
            <Shield size={24} />
            <div>
              <h2>イベント一覧</h2>
              <p>イベントごとの編集ページへ移動し、Itinerary / Presentation / Memories を編集します。</p>
            </div>
          </div>
          <EventListPanel
            events={events}
            media={media}
            presentationSlides={presentationSlides}
            onRefresh={onRefresh}
            onMessage={onMessage}
            selectedEventId={selectedAdminEventId}
            onSelectEvent={openEventEditor}
          />
        </div>

        <div className="admin-card admin-section-card" ref={membersRef}>
          <div className="admin-heading">
            <Users size={24} />
            <div>
              <h2>メンバー管理</h2>
              <p>Googleログインを許可するメールアドレスと権限を管理します。</p>
            </div>
          </div>
          <MembersEditor allowedUsers={allowedUsers} onRefresh={onRefreshAllowedUsers} onMessage={onMessage} />
        </div>

        <div className="admin-card admin-section-card" ref={settingsRef}>
          <div className="admin-heading">
            <Settings size={24} />
            <div>
              <h2>設定</h2>
              <p>現在の接続状態と運用情報を確認します。</p>
            </div>
          </div>
          <div className="settings-grid">
            <div>
              <span>データ接続</span>
              <strong>{isSupabaseConfigured ? "Supabase production" : "Demo mode"}</strong>
            </div>
            <div>
              <span>ログイン中</span>
              <strong>{profile?.email ?? userEmail}</strong>
            </div>
            <div>
              <span>権限</span>
              <strong>{profile?.role ?? "demo-admin"}</strong>
            </div>
            <div>
              <span>公開URL</span>
              <strong>family-events-cms.pages.dev</strong>
            </div>
          </div>
          <div className="resource-usage-panel">
            <div className="resource-usage-head">
              <div>
                <span className="eyebrow">Resource usage</span>
                <h3>アプリデータ使用状況</h3>
              </div>
              <p>アプリ側で把握できる件数を、運用目安に対する使用率として表示します。Storage容量などの正確な上限は各ダッシュボードで確認します。</p>
            </div>
            <div className="resource-usage-list">
              {resourceStats.map((stat) => {
                const usagePercent = Math.min(100, Math.round((stat.value / stat.limit) * 100));
                return (
                  <div className="resource-usage-row" key={stat.label}>
                    <div className="resource-usage-label">
                      <strong>{stat.label}</strong>
                      <span>{stat.detail}</span>
                    </div>
                    <div className="resource-usage-meter" aria-label={`${stat.label}: ${usagePercent}%`}>
                      <span style={{ width: `${Math.max(4, usagePercent)}%` }} />
                    </div>
                    <div className="resource-usage-value">
                      <strong>{stat.value}</strong>
                      <span>/ {stat.limit}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="resource-usage-note">目安を超えそうな場合は、写真容量、DBサイズ、Cloudflare Pagesのビルド状況を管理画面で確認します。</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function EditorView({
  event,
  events,
  sections,
  media,
  presentationSlides,
  presentationPreviewSlides,
  onPresentationPreviewChange,
  session,
  profile,
  mode,
  onModeChange,
  onBackToAdmin,
  onOpenPublic,
  onRefresh,
  onMessage
}: {
  event: FamilyEvent | null;
  events: FamilyEvent[];
  sections: EventSection[];
  media: MediaItem[];
  presentationSlides: PresentationSlide[];
  presentationPreviewSlides: PresentationSlide[];
  onPresentationPreviewChange: (eventId: string, slides: PresentationSlide[]) => void;
  session: Session | null;
  profile: Profile | null;
  mode: EventMode;
  onModeChange: (mode: EventMode) => void;
  onBackToAdmin: () => void;
  onOpenPublic: (eventSlug?: string, mode?: EventMode) => void;
  onRefresh: () => Promise<void>;
  onMessage: (message: string | null) => void;
}) {
  const workspaceRef = useRef<HTMLDivElement>(null);
  const editorTitle = {
    itinerary: {
      title: "Itinerary",
      description: "イベントの編集はItinerary画面で行います。"
    },
    presentation: {
      title: "Presentation",
      description: "Itineraryをもとに公開用プレゼンテーションを確認します。"
    },
    memories: {
      title: "Memories",
      description: "写真と動画を確認し、公開アルバムとして整理します。"
    }
  }[mode];
  const editorNav = [
    { mode: "itinerary", label: "Itinerary", icon: CalendarDays },
    { mode: "presentation", label: "Presentation", icon: Film },
    { mode: "memories", label: "Memories", icon: Camera }
  ] as const;

  if (!event) {
    return (
      <section className="editor-shell">
        <aside className="editor-sidebar">
          <EditorBrand eventTitle="イベント未選択" />
          <button className="editor-sidebar-item" type="button" onClick={onBackToAdmin}>
            <LayoutDashboard size={18} />
            イベント一覧
          </button>
        </aside>
        <main className="editor-main">
          <EmptyState title="編集するイベントがありません" body="管理画面でイベントを作成してください。" />
        </main>
      </section>
    );
  }

  return (
    <section className="editor-shell">
      <aside className="editor-sidebar">
        <EditorBrand eventTitle={event.title} />
        <button className="editor-sidebar-item" type="button" onClick={onBackToAdmin}>
          <LayoutDashboard size={18} />
          イベント一覧
        </button>
        {editorNav.map(({ mode: navMode, label, icon: Icon }) => (
          <button
            className={mode === navMode ? "editor-sidebar-item active" : "editor-sidebar-item"}
            key={navMode}
            type="button"
            onClick={() => onModeChange(navMode)}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
        <div className="editor-user-card">
          <Shield size={24} />
          <span>編集者としてログイン中</span>
          <strong>{profile?.email ?? session?.user.email ?? "demo@example.com"}</strong>
          <SignOutButton />
        </div>
      </aside>

      <main className="editor-main">
        <div className="editor-topline">
          <div>
            <h1>{editorTitle.title}</h1>
            <p>{editorTitle.description}</p>
          </div>
          <div className="editor-toolbar">
            <button type="button" onClick={() => onOpenPublic(event.slug, mode)}>
              <Eye size={18} />
              公開プレビュー
            </button>
          </div>
        </div>

        <EventWorkspace
          event={event}
          events={events}
          sections={sections}
          media={media}
          presentationSlides={presentationSlides}
          presentationPreviewSlides={presentationPreviewSlides}
          onPresentationPreviewChange={onPresentationPreviewChange}
          initialTab={mode}
          onTabChange={onModeChange}
          onBack={onBackToAdmin}
          onOpenPublic={onOpenPublic}
          onRefresh={onRefresh}
          onMessage={onMessage}
          workspaceRef={workspaceRef}
        />
      </main>
    </section>
  );
}

function EditorBrand({ eventTitle }: { eventTitle: string }) {
  return (
    <div className="editor-brand">
      <div>
        <Waves size={30} />
        <strong>編集画面</strong>
      </div>
      <span>{eventTitle}</span>
    </div>
  );
}

function EventWorkspace({
  event,
  events,
  sections,
  media,
  presentationSlides,
  presentationPreviewSlides,
  onPresentationPreviewChange,
  workspaceRef,
  initialTab,
  onTabChange,
  onBack,
  onOpenPublic,
  onRefresh,
  onMessage
}: {
  event: FamilyEvent;
  events: FamilyEvent[];
  sections: EventSection[];
  media: MediaItem[];
  presentationSlides: PresentationSlide[];
  presentationPreviewSlides: PresentationSlide[];
  onPresentationPreviewChange: (eventId: string, slides: PresentationSlide[]) => void;
  workspaceRef: React.RefObject<HTMLDivElement | null>;
  initialTab: EventMode;
  onTabChange: (tab: EventMode) => void;
  onBack: () => void;
  onOpenPublic: (eventSlug?: string, mode?: EventMode) => void;
  onRefresh: () => Promise<void>;
  onMessage: (message: string | null) => void;
}) {
  const [tab, setTab] = useState<EventMode>(initialTab);
  const eventSections = sections.filter((section) => section.event_id === event.id);
  const eventMedia = media.filter((item) => item.event_id === event.id);
  const eventPresentationSlides = presentationSlides
    .filter((slide) => slide.event_id === event.id)
    .sort((a, b) => a.sort_order - b.sort_order);
  const photos = eventMedia.filter((item) => item.type === "image");
  const [generatedSlides, setGeneratedSlides] = useState<GeneratedPresentationSlide[]>([]);
  const [presentationLoading, setPresentationLoading] = useState(false);
  const [presentationError, setPresentationError] = useState<string | null>(null);

  useEffect(() => {
    setTab(initialTab);
  }, [event.id, initialTab]);

  useEffect(() => {
    setGeneratedSlides(presentationPreviewSlides.map(presentationSlideToGeneratedSlide));
  }, [event.id, presentationPreviewSlides]);

  const switchTab = (value: EventMode) => {
    setTab(value);
    onTabChange(value);
  };

  const generatePresentation = async () => {
    setPresentationLoading(true);
    setPresentationError(null);
    try {
      const headers: HeadersInit = { "Content-Type": "application/json" };
      if (supabase) {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          throw new Error(`Supabaseセッション取得に失敗しました: ${error.message}`);
        }
        if (!data.session?.access_token) {
          throw new Error("Presentation生成にはログインが必要です。");
        }
        headers.Authorization = `Bearer ${data.session.access_token}`;
      }

      const response = await fetch("/api/gemini/presentation", {
        method: "POST",
        headers,
        body: JSON.stringify({
          event,
          itinerary: eventSections,
          media: eventMedia.map(({ title, type, caption }) => ({ title, type, caption }))
        })
      });
      const result = (await response.json()) as { slides?: GeneratedPresentationSlide[]; error?: string };
      if (!response.ok) {
        throw new Error(result.error ?? `Presentation generation failed (${response.status}).`);
      }
      setGeneratedSlides(result.slides ?? []);
      if (!result.slides?.length) {
        setPresentationError("Geminiからスライドが返されませんでした。");
      }
      onPresentationPreviewChange(
        event.id,
        (result.slides ?? []).map((slide, index) => generatedSlideToPresentationSlide(slide, event.id, index))
      );
    } catch (error) {
      setPresentationError(error instanceof Error ? error.message : "Presentation生成に失敗しました。");
    } finally {
      setPresentationLoading(false);
    }
  };

  const updateGeneratedSlide = (index: number, updates: Partial<Pick<GeneratedPresentationSlide, "title" | "body" | "badge">>) => {
    setGeneratedSlides((current) => {
      const next = current.map((slide, slideIndex) => (slideIndex === index ? { ...slide, ...updates } : slide));
      onPresentationPreviewChange(
        event.id,
        next.map((slide, slideIndex) => generatedSlideToPresentationSlide(slide, event.id, slideIndex))
      );
      return next;
    });
  };

  const saveGeneratedPresentation = async () => {
    if (generatedSlides.length === 0) {
      setPresentationError("保存する生成済みスライドがありません。");
      return;
    }
    if (!supabase) {
      setPresentationError("Supabase未設定のためDB保存はできません。生成結果はこの画面のプレビューで確認できます。");
      return;
    }

    setPresentationLoading(true);
    setPresentationError(null);
    const uploadedPaths: string[] = [];
    try {
      const rows = await Promise.all(
        generatedSlides.map(async (slide, index) => {
          let imageUrl = slide.imageUrl || null;
          if (imageUrl?.startsWith("data:") && !shouldKeepPresentationDataUrl(slide)) {
            const file = dataUrlToFile(imageUrl, `presentation-${index + 1}`);
            imageUrl = await uploadImageFile(event.id, file, "presentation");
            uploadedPaths.push(imageUrl);
          }
          return {
            event_id: event.id,
            section_kind: slide.sectionKind,
            title: slide.title,
            body: slide.body,
            badge: slide.badge || null,
            image_prompt: slide.imagePrompt || null,
            image_url: imageUrl,
            sort_order: slide.sortOrder ?? index * 10,
            generation_metadata: slide.generationMeta ?? null
          };
        })
      );

      const deleteResult = await supabase.from("presentation_slides").delete().eq("event_id", event.id);
      if (deleteResult.error) throw deleteResult.error;
      const insertResult = await supabase.from("presentation_slides").insert(rows);
      if (insertResult.error) throw insertResult.error;
      onMessage("Presentationスライドを保存しました。");
      await onRefresh();
    } catch (error) {
      if (uploadedPaths.length > 0) {
        await supabase.storage.from(MEDIA_BUCKET).remove(uploadedPaths);
      }
      setPresentationError(error instanceof Error ? error.message : "Presentation保存に失敗しました。");
    } finally {
      setPresentationLoading(false);
    }
  };

  return (
    <div className={`event-workspace ${tab}-workspace-shell`} ref={workspaceRef}>
      <div className="event-editor-breadcrumb">
        <button type="button" onClick={onBack}>
          ← イベント一覧
        </button>
        <span>›</span>
        <strong>{event.title}</strong>
      </div>
      <div className="workspace-hero">
        <div className="workspace-cover">
          <CoverImage src={event.cover_image_url} alt="" />
          {!event.cover_image_url && <ImageIcon size={34} />}
        </div>
        <div>
          <span className="eyebrow">Selected event</span>
          <h2>{event.title}</h2>
          <p>{event.description}</p>
          <div className="meta-row compact-meta">
            <span>
              <CalendarDays size={16} />
              {formatDateRange(event)}
            </span>
            {event.location && (
              <span>
                <MapPin size={16} />
                {event.location}
              </span>
            )}
          </div>
        </div>
      </div>

      {tab === "itinerary" && (
        <div className="itinerary-workbench">
          <div>
            <div className="workspace-section-title">
              <Shield size={20} />
              <div>
                <h3>イベント基本情報</h3>
                <p>旅程の前提になるタイトル、日程、場所、カバー画像を編集します。</p>
              </div>
            </div>
            <EventEditor
              events={events}
              media={media}
              onRefresh={onRefresh}
              onMessage={onMessage}
              focusEventId={event.id}
              showList={false}
            />
          </div>
          <div>
            <div className="workspace-section-title">
              <CalendarDays size={20} />
              <div>
                <h3>旅程セクション</h3>
                <p>このItineraryをもとにPresentationを作り、最後にMemoriesで振り返ります。</p>
              </div>
            </div>
            <SectionEditor
              events={events}
              sections={sections}
              onRefresh={onRefresh}
              onMessage={onMessage}
              lockedEventId={event.id}
            />
            <div className="workspace-next-row">
              <span>Itineraryを完成させたら、次はPresentationで公開ページの見え方を確認します。</span>
              <button type="button" onClick={() => onOpenPublic(event.slug, "itinerary")}>
                <Eye size={18} />
                公開プレビュー
              </button>
              <button type="button" onClick={() => switchTab("presentation")}>
                次へ: Presentation
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "presentation" && (
        <div className="presentation-builder">
          <section className="presentation-builder-preview">
            <div className="mini-presentation">
              <CoverImage src={generatedSlides[0]?.imageUrl ?? eventPresentationSlides[0]?.image_url ?? event.cover_image_url} alt="" />
              <div>
                <span>{generatedSlides[0]?.badge ?? eventPresentationSlides[0]?.badge ?? "Family Trip"}</span>
                <h3>{generatedSlides[0]?.title ?? eventPresentationSlides[0]?.title ?? event.title}</h3>
                <p>{generatedSlides[0]?.body ?? eventPresentationSlides[0]?.body ?? event.description}</p>
              </div>
            </div>
            <div className="mini-slide-list">
              {(generatedSlides.length > 0 ? generatedSlides : eventPresentationSlides).slice(0, 6).map((slide, index) => (
                <article key={`${slide.title}-${index}`}>
                  <CoverImage src={"imageUrl" in slide ? slide.imageUrl : slide.image_url} alt="" />
                  <div>
                    <strong>{"sectionKind" in slide ? slide.sectionKind : slide.section_kind}</strong>
                    <span>{slide.title}</span>
                    <small>{slide.body}</small>
                  </div>
                </article>
              ))}
              {generatedSlides.length === 0 && eventPresentationSlides.length === 0 && (
                <article>
                  <CoverImage src={event.cover_image_url} alt="" />
                  <div>
                    <strong>itinerary</strong>
                    <span>保存済みPresentationはまだありません</span>
                    <small>Geminiで生成すると、ここにプレビューが表示されます。</small>
                  </div>
                </article>
              )}
            </div>
            {generatedSlides.length > 0 && (
              <div className="presentation-text-editor">
                <h4>生成テキスト編集</h4>
                {generatedSlides.map((slide, index) => (
                  <article key={`${slide.sortOrder}-${index}`}>
                    <label>
                      バッジ
                      <input
                        value={slide.badge}
                        onChange={(event) => updateGeneratedSlide(index, { badge: event.target.value })}
                      />
                    </label>
                    <label>
                      タイトル
                      <input
                        value={slide.title}
                        onChange={(event) => updateGeneratedSlide(index, { title: event.target.value })}
                      />
                    </label>
                    <label>
                      本文
                      <textarea
                        rows={3}
                        value={slide.body}
                        onChange={(event) => updateGeneratedSlide(index, { body: event.target.value })}
                      />
                    </label>
                  </article>
                ))}
              </div>
            )}
          </section>
          <aside className="presentation-settings-panel">
            <h3>Presentation生成</h3>
            <label>
              プレゼンの雰囲気
              <select defaultValue="casual">
                <option value="casual">明るくカジュアル</option>
                <option value="cinematic">シネマティック</option>
                <option value="quiet">落ち着いた記録</option>
              </select>
            </label>
            <label>
              画像比率
              <select defaultValue="square" disabled aria-label="画像比率は1:1固定">
                <option value="square">1:1（スクエア固定）</option>
              </select>
            </label>
            <div className="presentation-metrics compact">
              <div>
                <strong>{eventSections.length}</strong>
                <span>旅程</span>
              </div>
              <div>
                <strong>{photos.length}</strong>
                <span>写真</span>
              </div>
              <div>
                <strong>{eventPresentationSlides.length}</strong>
                <span>保存済み</span>
              </div>
            </div>
            {presentationError && <p className="form-error">{presentationError}</p>}
            {!isSupabaseConfigured && generatedSlides.length > 0 && (
              <p className="demo-save-note">Demo modeではDB保存せず、この画面だけで生成プレビューを保持します。</p>
            )}
            <button className="primary" type="button" disabled={presentationLoading} onClick={generatePresentation}>
              <Film size={18} />
              {presentationLoading ? "生成中..." : "プレゼンテーションを生成"}
            </button>
            <button type="button" onClick={() => onOpenPublic(event.slug, "presentation")}>
              <Eye size={18} />
              公開プレビュー
            </button>
            <button type="button" disabled={presentationLoading || generatedSlides.length === 0} onClick={saveGeneratedPresentation}>
              <Save size={18} />
              保存
            </button>
            <button className="primary" type="button" onClick={() => switchTab("memories")}>
              次へ: Memories
              <ChevronRight size={18} />
            </button>
          </aside>
        </div>
      )}

      {tab === "memories" && (
        <div className="memories-workbench">
          <div className="workspace-section-title">
            <Camera size={20} />
            <div>
              <h3>写真・動画</h3>
              <p>Presentationを見たあとに、写真と動画を確認・追加してアルバム化します。</p>
            </div>
            <button type="button" onClick={() => onOpenPublic(event.slug, "memories")}>
              <Eye size={18} />
              公開プレビュー
            </button>
          </div>
          <MediaEditor
            events={events}
            sections={sections}
            media={media}
            onRefresh={onRefresh}
            onMessage={onMessage}
            lockedEventId={event.id}
          />
        </div>
      )}
    </div>
  );
}

function LoginPanel({
  title,
  description,
  onMessage
}: {
  title: string;
  description?: string;
  onMessage: (message: string | null) => void;
}) {
  const [loading, setLoading] = useState(false);

  const signInWithGoogle = async () => {
    if (!supabase) {
      onMessage("Google認証にはSupabase設定が必要です。");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin
      }
    });
    setLoading(false);
    if (error) onMessage(`Googleログインに失敗しました: ${error.message}`);
  };

  return (
    <section className="login-screen">
      <div className="login-visual">
        <div className="login-visual-copy">
          <span className="eyebrow">Private family portal</span>
          <h1>Ikeda Family Events</h1>
          <p>旅の予定、写真、動画を家族だけで共有します。</p>
        </div>
      </div>
      <div className="login-panel admin-card">
        <div className="login-icon">
          <LogIn size={24} />
        </div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
        <div className="form-grid">
          <button className="primary google-login-button" type="button" disabled={loading} onClick={signInWithGoogle}>
            <LogIn size={18} />
            {loading ? "Googleへ移動中..." : "Googleでログイン"}
          </button>
          <p className="login-note">
            管理者として利用できるアカウントは <strong>{ADMIN_EMAIL}</strong> です。
          </p>
        </div>
      </div>
    </section>
  );
}

function EventListPanel({
  events,
  media,
  presentationSlides,
  selectedEventId,
  onSelectEvent,
  onRefresh,
  onMessage
}: {
  events: FamilyEvent[];
  media: MediaItem[];
  presentationSlides: PresentationSlide[];
  selectedEventId: string;
  onSelectEvent: (eventId: string, mode?: "itinerary" | "presentation" | "memories") => void;
  onRefresh: () => Promise<void>;
  onMessage: (message: string | null) => void;
}) {
  const createEvent = async () => {
    if (!supabase) {
      onMessage("イベント作成にはSupabase設定が必要です。");
      return;
    }

    const timestamp = Date.now();
    const payload = {
      title: "新しいイベント",
      slug: `event-${timestamp}`,
      description: "",
      location: null,
      icon: "calendar",
      status: "draft" as const,
      start_date: null,
      end_date: null,
      cover_image_url: null,
      is_published: false,
      sort_order: events.length * 10 + 100
    };

    const { data, error } = await supabase.from("events").insert(payload).select("id").single();
    if (error) {
      onMessage(`イベント作成に失敗しました: ${error.message}`);
      return;
    }

    onMessage("新しいイベントを作成しました。Itineraryで内容を編集してください。");
    await onRefresh();
    if (data?.id) onSelectEvent(data.id, "itinerary");
  };

  const removeEvent = async (eventId: string) => {
    if (!supabase) {
      onMessage("削除にはSupabase設定が必要です。");
      return;
    }

    const target = events.find((event) => event.id === eventId);
    if (!window.confirm(`${target?.title ?? "このイベント"}を削除します。関連する旅程・写真・動画も削除されます。`)) {
      return;
    }

    const storagePaths = media
      .filter((item) => item.event_id === eventId && isStorageImagePath(item))
      .map((item) => item.url);
    if (target?.cover_image_url && isStoragePath(target.cover_image_url)) {
      storagePaths.push(target.cover_image_url);
    }

    const { error } = await supabase.from("events").delete().eq("id", eventId);
    if (error) {
      onMessage(`イベント削除に失敗しました: ${error.message}`);
      return;
    }

    if (storagePaths.length > 0) {
      const storageResult = await supabase.storage.from(MEDIA_BUCKET).remove(storagePaths);
      if (storageResult.error) {
        onMessage(`イベントは削除しましたが、Storage削除に失敗しました: ${storageResult.error.message}`);
        await onRefresh();
        return;
      }
    }

    onMessage("イベントを削除しました。");
    await onRefresh();
    const nextEvent = events.find((event) => event.id !== eventId);
    if (nextEvent) onSelectEvent(nextEvent.id, "itinerary");
  };

  return (
    <div className="event-list-panel">
      <div className="table-list event-picker-list">
        <div className="table-list-head">
          <h3>イベント一覧</h3>
          <button type="button" onClick={createEvent}>
            <Plus size={18} />
            新規イベント
          </button>
        </div>
        <div className="table-header event-picker-header">
          <span>タイトル</span>
          <span>日程</span>
          <span>公開</span>
          <span>操作</span>
        </div>
        {events.map((event) => (
          <div
            className={event.id === selectedEventId ? "table-row event-picker-row selected" : "table-row event-picker-row"}
            key={event.id}
          >
            <span>
              <strong>{event.title}</strong>
              <small>{event.slug}</small>
              <small>{presentationSlides.filter((slide) => slide.event_id === event.id).length} presentation slides</small>
            </span>
            <span className="date-cell">{formatDateRange(event)}</span>
            <span className={event.is_published ? "badge live" : "badge"}>{event.is_published ? "公開" : "下書き"}</span>
            <div className="row-actions">
              <button type="button" onClick={() => onSelectEvent(event.id, "itinerary")} aria-label={`${event.title}をItineraryで編集`}>
                <Edit size={18} />
                Itinerary
              </button>
              <button type="button" onClick={() => onSelectEvent(event.id, "presentation")} aria-label={`${event.title}をPresentationで編集`}>
                <Film size={18} />
                Presentation
              </button>
              <button type="button" onClick={() => onSelectEvent(event.id, "memories")} aria-label={`${event.title}をMemoriesで編集`}>
                <Camera size={18} />
                Memories
              </button>
              <button type="button" onClick={() => removeEvent(event.id)} aria-label={`${event.title}を削除`}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventEditor({
  events,
  media,
  onRefresh,
  onMessage,
  onSelectEvent,
  focusEventId,
  showList = true
}: {
  events: FamilyEvent[];
  media: MediaItem[];
  onRefresh: () => Promise<void>;
  onMessage: (message: string | null) => void;
  onSelectEvent?: (eventId: string) => void;
  focusEventId?: string;
  showList?: boolean;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EventInput>(emptyEvent);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (!focusEventId) return;
    const target = events.find((event) => event.id === focusEventId);
    if (target) edit(target);
  }, [events, focusEventId]);

  const edit = (event: FamilyEvent) => {
    setEditingId(event.id);
    setCoverFile(null);
    setForm({
      title: event.title,
      slug: event.slug,
      description: event.description,
      location: event.location ?? "",
      icon: event.icon,
      status: event.status,
      start_date: event.start_date ?? "",
      end_date: event.end_date ?? "",
      cover_image_url: event.cover_image_url ?? "",
      is_published: event.is_published,
      sort_order: event.sort_order
    });
  };

  const reset = () => {
    if (focusEventId) {
      const target = events.find((event) => event.id === focusEventId);
      if (target) edit(target);
      return;
    }
    setEditingId(null);
    setForm(emptyEvent);
    setCoverFile(null);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase) {
      onMessage("保存にはSupabase設定が必要です。");
      return;
    }

    setUploadingCover(true);
    let coverImageUrl = form.cover_image_url || null;
    const currentEvent = editingId ? events.find((event) => event.id === editingId) : null;

    if (coverFile) {
      try {
        coverImageUrl = await uploadImageFile(editingId ?? crypto.randomUUID(), coverFile, "cover");
      } catch (error) {
        setUploadingCover(false);
        onMessage(`カバー画像アップロードに失敗しました: ${error instanceof Error ? error.message : "unknown error"}`);
        return;
      }
    }

    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      location: form.location || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      cover_image_url: coverImageUrl
    };

    const result = editingId
      ? await supabase.from("events").update(payload).eq("id", editingId)
      : await supabase.from("events").insert(payload);
    setUploadingCover(false);

    if (result.error) {
      onMessage(`保存に失敗しました: ${result.error.message}`);
      return;
    }

    if (currentEvent?.cover_image_url && isStoragePath(currentEvent.cover_image_url) && currentEvent.cover_image_url !== coverImageUrl) {
      await supabase.storage.from(MEDIA_BUCKET).remove([currentEvent.cover_image_url]);
    }

    onMessage("イベントを保存しました。");
    if (!focusEventId) reset();
    await onRefresh();
  };

  const remove = async (eventId: string) => {
    if (!supabase) {
      onMessage("削除にはSupabase設定が必要です。");
      return;
    }
    const target = events.find((event) => event.id === eventId);
    if (!window.confirm(`${target?.title ?? "このイベント"}を削除します。関連する旅程・メディアも削除されます。`)) {
      return;
    }

    const storagePaths = media
      .filter((item) => item.event_id === eventId && isStorageImagePath(item))
      .map((item) => item.url);
    if (target?.cover_image_url && isStoragePath(target.cover_image_url)) {
      storagePaths.push(target.cover_image_url);
    }
    const { error } = await supabase.from("events").delete().eq("id", eventId);
    if (error) {
      onMessage(`削除に失敗しました: ${error.message}`);
      return;
    }
    if (storagePaths.length > 0) {
      const storageResult = await supabase.storage.from(MEDIA_BUCKET).remove(storagePaths);
      if (storageResult.error) {
        onMessage(`イベントは削除しましたが、Storage削除に失敗しました: ${storageResult.error.message}`);
        await onRefresh();
        return;
      }
    }
    onMessage("イベントを削除しました。");
    if (editingId === eventId) reset();
    await onRefresh();
  };

  return (
    <div className={showList ? "editor-grid" : "editor-grid single-editor-grid"}>
      <form className="form-grid" onSubmit={save}>
        <label>
          タイトル
          <input
            value={form.title}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                title: event.target.value,
                slug: current.slug || slugify(event.target.value)
              }))
            }
            required
          />
        </label>
        <label>
          スラッグ
          <input value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} required />
        </label>
        <label>
          説明
          <textarea
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
            rows={4}
          />
        </label>
        <div className="two-cols">
          <label>
            開始日
            <input
              value={form.start_date ?? ""}
              onChange={(event) => setForm({ ...form, start_date: event.target.value })}
              type="date"
            />
          </label>
          <label>
            終了日
            <input
              value={form.end_date ?? ""}
              onChange={(event) => setForm({ ...form, end_date: event.target.value })}
              type="date"
            />
          </label>
        </div>
        <label>
          場所
          <input value={form.location ?? ""} onChange={(event) => setForm({ ...form, location: event.target.value })} />
        </label>
        <label>
          カバー画像
          <input accept="image/*" onChange={(event) => setCoverFile(event.target.files?.[0] ?? null)} type="file" />
          <small>アップロードすると公開ページのカード・詳細ヘッダーに表示されます。</small>
        </label>
        <div className="two-cols">
          <label>
            状態
            <select
              value={form.status}
              onChange={(event) => setForm({ ...form, status: event.target.value as EventInput["status"] })}
            >
              <option value="draft">draft</option>
              <option value="upcoming">upcoming</option>
              <option value="completed">completed</option>
            </select>
          </label>
          <label>
            並び順
            <input
              value={form.sort_order}
              onChange={(event) => setForm({ ...form, sort_order: Number(event.target.value) })}
              type="number"
            />
          </label>
        </div>
        <label className="check-row">
          <input
            checked={form.is_published}
            onChange={(event) => setForm({ ...form, is_published: event.target.checked })}
            type="checkbox"
          />
          公開する
        </label>
        <div className="button-row">
          <button className="primary" type="submit" disabled={uploadingCover}>
            <Save size={18} />
            {uploadingCover ? "保存中..." : "保存"}
          </button>
          <button type="button" onClick={reset}>
            <Plus size={18} />
            新規
          </button>
        </div>
      </form>
      {showList && (
      <div className="table-list">
        <div className="table-list-head">
          <h3>イベント一覧</h3>
          <button type="button" onClick={reset}>
            <Plus size={18} />
            新規イベント
          </button>
        </div>
        <div className="table-header">
          <span>タイトル</span>
          <span>日程</span>
          <span>公開</span>
          <span>操作</span>
        </div>
        {events.map((event) => (
          <div className="table-row" key={event.id}>
            <span>
              <strong>{event.title}</strong>
              <small>{event.slug}</small>
            </span>
            <span className="date-cell">{formatDateRange(event)}</span>
            <span className={event.is_published ? "badge live" : "badge"}>{event.is_published ? "公開" : "下書き"}</span>
            <button
              type="button"
              onClick={() => {
                edit(event);
                onSelectEvent?.(event.id);
              }}
              aria-label={`${event.title}を編集`}
            >
              <Edit size={18} />
            </button>
            <button type="button" onClick={() => remove(event.id)} aria-label={`${event.title}を削除`}>
              <Trash2 size={18} />
            </button>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

type SectionForm = {
  event_id: string;
  day_label: string;
  time_label: string;
  title: string;
  location: string;
  body: string;
  sort_order: number;
};

function SectionEditor({
  events,
  sections,
  onRefresh,
  onMessage,
  lockedEventId
}: {
  events: FamilyEvent[];
  sections: EventSection[];
  onRefresh: () => Promise<void>;
  onMessage: (message: string | null) => void;
  lockedEventId?: string;
}) {
  const initialEventId = lockedEventId || events[0]?.id || "";
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draggingSectionId, setDraggingSectionId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; position: "before" | "after" } | null>(null);
  const [form, setForm] = useState<SectionForm>({
    event_id: initialEventId,
    day_label: "Day 1",
    time_label: "",
    title: "",
    location: "",
    body: "",
    sort_order: 10
  });

  useEffect(() => {
    if (lockedEventId && form.event_id !== lockedEventId) {
      setEditingId(null);
      setForm({
        event_id: lockedEventId,
        day_label: "Day 1",
        time_label: "",
        title: "",
        location: "",
        body: "",
        sort_order: 10
      });
      return;
    }
    if (!form.event_id && events[0]) {
      setForm((current) => ({ ...current, event_id: events[0].id }));
    }
  }, [events, form.event_id, lockedEventId]);

  const selectedSections = [...sections]
    .filter((section) => section.event_id === form.event_id)
    .sort((a, b) => a.sort_order - b.sort_order);

  const reset = () => {
    setEditingId(null);
    setForm({
      event_id: form.event_id || initialEventId,
      day_label: "Day 1",
      time_label: "",
      title: "",
      location: "",
      body: "",
      sort_order: selectedSections.length * 10 + 10
    });
  };

  const edit = (section: EventSection) => {
    const action = parseItineraryAction(section);
    setEditingId(section.id);
    setForm({
      event_id: section.event_id,
      day_label: action.dayLabel,
      time_label: action.timeLabel,
      title: action.actionTitle,
      location: action.location,
      body: action.note,
      sort_order: section.sort_order
    });
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase) {
      onMessage("旅程セクションの保存にはSupabase設定が必要です。");
      return;
    }

    const sectionContent = buildItinerarySection({
      dayLabel: form.day_label,
      timeLabel: form.time_label.trim(),
      actionTitle: form.title.trim(),
      location: form.location.trim(),
      note: form.body.trim()
    });
    const payload = {
      event_id: form.event_id,
      title: sectionContent.title,
      body: sectionContent.body,
      sort_order: form.sort_order
    };

    const result = editingId
      ? await supabase.from("event_sections").update(payload).eq("id", editingId)
      : await supabase.from("event_sections").insert(payload);

    if (result.error) {
      onMessage(`旅程セクションの保存に失敗しました: ${result.error.message}`);
      return;
    }

    onMessage("旅程セクションを保存しました。");
    reset();
    await onRefresh();
  };

  const remove = async (sectionId: string) => {
    if (!supabase) {
      onMessage("削除にはSupabase設定が必要です。");
      return;
    }
    const target = sections.find((section) => section.id === sectionId);
    if (!window.confirm(`${target?.title ?? "この旅程セクション"}を削除します。`)) {
      return;
    }

    const { error } = await supabase.from("event_sections").delete().eq("id", sectionId);
    if (error) {
      onMessage(`旅程セクション削除に失敗しました: ${error.message}`);
      return;
    }

    onMessage("旅程セクションを削除しました。");
    if (editingId === sectionId) reset();
    await onRefresh();
  };

  const reorderSections = async (targetSectionId: string, position: "before" | "after") => {
    if (!draggingSectionId || draggingSectionId === targetSectionId) {
      setDraggingSectionId(null);
      setDropTarget(null);
      return;
    }
    if (!supabase) {
      onMessage("並べ替えの保存にはSupabase設定が必要です。");
      setDraggingSectionId(null);
      setDropTarget(null);
      return;
    }
    const client = supabase;

    const fromIndex = selectedSections.findIndex((section) => section.id === draggingSectionId);
    const toIndex = selectedSections.findIndex((section) => section.id === targetSectionId);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggingSectionId(null);
      setDropTarget(null);
      return;
    }

    const nextSections = [...selectedSections];
    const [movedSection] = nextSections.splice(fromIndex, 1);
    const adjustedTargetIndex = nextSections.findIndex((section) => section.id === targetSectionId);
    nextSections.splice(position === "after" ? adjustedTargetIndex + 1 : adjustedTargetIndex, 0, movedSection);
    const updates = nextSections.map((section, index) => ({
      id: section.id,
      sort_order: (index + 1) * 10
    }));

    const results = await Promise.all(
      updates.map((update) => client.from("event_sections").update({ sort_order: update.sort_order }).eq("id", update.id))
    );
    const failed = results.find((result) => result.error);
    setDraggingSectionId(null);
    setDropTarget(null);
    if (failed?.error) {
      onMessage(`旅程の並べ替えに失敗しました: ${failed.error.message}`);
      return;
    }
    onMessage("旅程の並び順を更新しました。");
    await onRefresh();
  };

  return (
    <div className="editor-grid">
      <form className="form-grid" onSubmit={save}>
        {!lockedEventId && (
          <label>
            イベント
            <select
              value={form.event_id}
              onChange={(event) => {
                setEditingId(null);
                setForm({
                  event_id: event.target.value,
                  day_label: "Day 1",
                  time_label: "",
                  title: "",
                  location: "",
                  body: "",
                  sort_order: 10
                });
              }}
              required
            >
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="itinerary-action-form">
          <label>
            日程
            <select value={form.day_label} onChange={(event) => setForm({ ...form, day_label: event.target.value })}>
              {[...defaultDayLabels, "出発前", "帰宅後"].map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </label>
          <label>
            時刻
            <input
              value={form.time_label}
              onChange={(event) => setForm({ ...form, time_label: event.target.value })}
              placeholder="09:00"
            />
          </label>
        </div>
        <label>
          行動名
          <input
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
            placeholder="石垣空港に到着"
            required
          />
        </label>
        <label>
          場所
          <input
            value={form.location}
            onChange={(event) => setForm({ ...form, location: event.target.value })}
            placeholder="石垣空港"
          />
        </label>
        <label>
          メモ
          <textarea
            value={form.body}
            onChange={(event) => setForm({ ...form, body: event.target.value })}
            placeholder="集合場所、予約番号、持ち物など"
            rows={5}
          />
        </label>
        <div className="button-row">
          <button className="primary" type="submit">
            <Save size={18} />
            {editingId ? "更新" : "追加"}
          </button>
          <button type="button" onClick={reset}>
            <Plus size={18} />
            新規
          </button>
        </div>
      </form>
      <div className="table-list">
        <div className="table-list-head">
          <h3>行動別の旅程一覧</h3>
          <button type="button" onClick={reset}>
            <Plus size={18} />
            行動を追加
          </button>
        </div>
        <div className="table-header compact-table-header">
          <span>行動</span>
          <span>並べ替え</span>
          <span>操作</span>
        </div>
        {selectedSections.map((section) => {
          const action = parseItineraryAction(section);
          const dropClass =
            dropTarget?.id === section.id && draggingSectionId !== section.id
              ? dropTarget.position === "before"
                ? " drop-before"
                : " drop-after"
              : "";
          return (
            <div
              className={`${draggingSectionId === section.id ? "table-row compact-table-row dragging" : "table-row compact-table-row"}${dropClass}`}
              draggable
              key={section.id}
              onDragEnd={() => {
                setDraggingSectionId(null);
                setDropTarget(null);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                const rect = event.currentTarget.getBoundingClientRect();
                const position = event.clientY < rect.top + rect.height / 2 ? "before" : "after";
                setDropTarget({ id: section.id, position });
              }}
              onDragStart={() => setDraggingSectionId(section.id)}
              onDrop={() => reorderSections(section.id, dropTarget?.id === section.id ? dropTarget.position : "before")}
            >
              <span>
                <span className="itinerary-list-meta">
                  <b>{action.dayLabel} {action.timeLabel || "時間未定"}</b>
                </span>
                <strong>{action.actionTitle}</strong>
                {action.location && <small>{action.location}</small>}
              </span>
              <span className="drag-handle" title="ドラッグして並べ替え">
                <GripVertical size={20} />
              </span>
              <button type="button" onClick={() => edit(section)} aria-label={`${section.title}を編集`}>
                <Edit size={18} />
              </button>
              <button type="button" onClick={() => remove(section.id)} aria-label={`${section.title}を削除`}>
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MediaEditor({
  events,
  sections,
  media,
  onRefresh,
  onMessage,
  lockedEventId
}: {
  events: FamilyEvent[];
  sections: EventSection[];
  media: MediaItem[];
  onRefresh: () => Promise<void>;
  onMessage: (message: string | null) => void;
  lockedEventId?: string;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [eventId, setEventId] = useState(lockedEventId || events[0]?.id || "");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [caption, setCaption] = useState("");
  const [actionLabel, setActionLabel] = useState("");
  const [type, setType] = useState<MediaItem["type"]>("youtube");
  const [imageUploadMode, setImageUploadMode] = useState<"single" | "bulk">("single");
  const [sortOrder, setSortOrder] = useState(10);
  const [uploading, setUploading] = useState(false);
  const [selectedPreviewId, setSelectedPreviewId] = useState<string | null>(null);
  const [showMediaAddPanel, setShowMediaAddPanel] = useState(false);
  const mediaFormRef = useRef<HTMLFormElement | null>(null);
  const mediaTitleInputRef = useRef<HTMLInputElement | null>(null);
  const mediaUrlInputRef = useRef<HTMLInputElement | null>(null);
  const mediaFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (lockedEventId && eventId !== lockedEventId) {
      setEventId(lockedEventId);
      setEditingId(null);
      setShowMediaAddPanel(false);
      return;
    }
    if (!eventId && events[0]) setEventId(events[0].id);
  }, [eventId, events, lockedEventId]);

  const selectedMedia = media.filter((item) => item.event_id === eventId);
  const selectedSections = sections.filter((section) => section.event_id === eventId);
  const selectedPreview = selectedMedia.find((item) => item.id === selectedPreviewId) ?? selectedMedia[0] ?? null;

  useEffect(() => {
    if (!selectedPreviewId && selectedMedia[0]) {
      setSelectedPreviewId(selectedMedia[0].id);
      return;
    }
    if (selectedPreviewId && !selectedMedia.some((item) => item.id === selectedPreviewId)) {
      setSelectedPreviewId(selectedMedia[0]?.id ?? null);
    }
  }, [selectedMedia, selectedPreviewId]);

  useEffect(() => {
    if (!showMediaAddPanel) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        reset();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showMediaAddPanel]);

  const reset = () => {
    setShowMediaAddPanel(false);
    setEditingId(null);
    setTitle("");
    setUrl("");
    setFiles([]);
    setCaption("");
    setActionLabel("");
    setType("youtube");
    setImageUploadMode("single");
    setSortOrder(selectedMedia.length * 10 + 10);
  };

  const focusMediaForm = (nextType: MediaItem["type"]) => {
    window.setTimeout(() => {
      mediaFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (nextType === "youtube") {
        mediaUrlInputRef.current?.focus();
      } else {
        mediaFileInputRef.current?.focus();
      }
    }, 0);
  };

  const startNewMedia = (
    nextType: MediaItem["type"],
    nextImageMode: "single" | "bulk" = "single",
    nextActionLabel = ""
  ) => {
    setShowMediaAddPanel(true);
    setEditingId(null);
    setTitle("");
    setUrl("");
    setFiles([]);
    setCaption("");
    setActionLabel(nextActionLabel);
    setType(nextType);
    setImageUploadMode(nextImageMode);
    setSortOrder(selectedMedia.length * 10 + 10);
    focusMediaForm(nextType);
  };

  const edit = (item: MediaItem) => {
    const captionInfo = splitMediaCaption(item.caption);
    setShowMediaAddPanel(true);
    setEditingId(item.id);
    setSelectedPreviewId(item.id);
    setEventId(item.event_id);
    setTitle(item.title);
    setUrl(item.type === "youtube" ? item.url : "");
    setFiles([]);
    setCaption(captionInfo.caption);
    setActionLabel(captionInfo.actionLabel);
    setType(item.type);
    setImageUploadMode("single");
    setSortOrder(item.sort_order);
    focusMediaForm(item.type);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase) {
      onMessage("写真・動画の追加にはSupabase設定が必要です。");
      return;
    }

    setUploading(true);
    let mediaUrl = url;
    const currentItem = editingId ? media.find((item) => item.id === editingId) : null;

    if (type === "image") {
      if (files.length === 0 && !currentItem) {
        onMessage("写真ファイルを選択してください。");
        setUploading(false);
        return;
      }

      if (!editingId && files.length > 1) {
        try {
          const uploads = await Promise.all(
            files.map(async (imageFile, index) => ({
              event_id: eventId,
              title: title ? `${title} ${index + 1}` : fileBaseName(imageFile.name),
              url: await uploadImageFile(eventId, imageFile),
              caption: buildMediaCaption(caption, actionLabel),
              type,
              sort_order: sortOrder + index * 10
            }))
          );
          const result = await supabase.from("media_items").insert(uploads);
          setUploading(false);
          if (result.error) {
            await supabase.storage.from(MEDIA_BUCKET).remove(uploads.map((item) => item.url));
            onMessage(`写真アップロードに失敗しました: ${result.error.message}`);
            return;
          }
          reset();
          onMessage(`${uploads.length}枚の写真を追加しました。`);
          await onRefresh();
          return;
        } catch (error) {
          setUploading(false);
          onMessage(`写真アップロードに失敗しました: ${error instanceof Error ? error.message : "unknown error"}`);
          return;
        }
      }

      if (files[0]) {
        try {
          mediaUrl = await uploadImageFile(eventId, files[0]);
        } catch (error) {
          onMessage(`写真アップロードに失敗しました: ${error instanceof Error ? error.message : "unknown error"}`);
          setUploading(false);
          return;
        }
      } else if (currentItem) {
        mediaUrl = currentItem.url;
      }
    }

    const payload = {
      event_id: eventId,
      title: title || (files[0] ? fileBaseName(files[0].name) : ""),
      url: mediaUrl,
      caption: buildMediaCaption(caption, actionLabel),
      type,
      sort_order: sortOrder
    };

    const result = editingId
      ? await supabase.from("media_items").update(payload).eq("id", editingId)
      : await supabase.from("media_items").insert(payload);
    setUploading(false);
    if (result.error) {
      onMessage(`メディア保存に失敗しました: ${result.error.message}`);
      return;
    }

    if (currentItem && isStorageImagePath(currentItem) && (type !== "image" || currentItem.url !== mediaUrl)) {
      await supabase.storage.from(MEDIA_BUCKET).remove([currentItem.url]);
    }

    reset();
    onMessage(editingId ? "メディアを更新しました。" : "メディアを追加しました。");
    await onRefresh();
  };

  const remove = async (item: MediaItem) => {
    if (!supabase) {
      onMessage("削除にはSupabase設定が必要です。");
      return;
    }
    if (!window.confirm(`${item.title}を削除します。写真の場合はStorage上のファイルも削除されます。`)) {
      return;
    }
    const { error } = await supabase.from("media_items").delete().eq("id", item.id);
    if (error) {
      onMessage(`メディア削除に失敗しました: ${error.message}`);
      return;
    }
    if (isStorageImagePath(item)) {
      const storageResult = await supabase.storage.from(MEDIA_BUCKET).remove([item.url]);
      if (storageResult.error) {
        onMessage(`DBから削除しましたが、Storage削除に失敗しました: ${storageResult.error.message}`);
        await onRefresh();
        return;
      }
    }
    if (editingId === item.id) reset();
    onMessage("メディアを削除しました。");
    await onRefresh();
  };

  const selectedPreviewCaption = splitMediaCaption(selectedPreview?.caption);
  const mediaGroups = [
    ...selectedSections.map((section, index) => {
      const action = parseItineraryAction(section);
      const groupItems = selectedMedia.filter((item) => splitMediaCaption(item.caption).actionLabel === section.title);
      return {
        id: section.id,
        actionLabel: section.title,
        title: action.actionTitle,
        dayLabel: action.dayLabel,
        timeLabel: action.timeLabel || sectionTimeLabel(section, index),
        location: action.location,
        items: groupItems
      };
    }),
    {
      id: "unassigned",
      actionLabel: "",
      title: "未分類",
      dayLabel: "",
      timeLabel: "",
      location: "",
      items: selectedMedia.filter((item) => !splitMediaCaption(item.caption).actionLabel)
    }
  ].filter((group) => group.id !== "unassigned" || group.items.length > 0);

  if (lockedEventId) {
    return (
      <div className="media-gallery-editor">
        <div className="media-gallery-toolbar">
          <div className="media-filter-group" aria-label="メディア種別">
            <button className="active" type="button">
              すべて <span>{selectedMedia.length}</span>
            </button>
            <button type="button">
              <ImageIcon size={16} />
              写真 <span>{selectedMedia.filter((item) => item.type === "image").length}</span>
            </button>
            <button type="button">
              <Film size={16} />
              動画 <span>{selectedMedia.filter((item) => item.type === "youtube").length}</span>
            </button>
          </div>
        </div>

        {showMediaAddPanel && (
          <div className="media-composer-overlay" role="presentation" onClick={reset}>
            <section
              className="media-composer-modal"
              role="dialog"
              aria-modal="true"
              aria-label={editingId ? "メディア編集" : "メディア追加"}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="media-add-panel-head">
                <div>
                  <strong>{editingId ? "選択中メディアを編集" : "写真・動画を追加"}</strong>
                  <p>
                    {editingId
                      ? "右側のプレビューを見ながら、タイトルやキャプション、旅程との紐づけを更新します。"
                      : "新しい写真やYouTube動画を、このイベントの旅程ごとに追加します。"}
                  </p>
                </div>
                <button className="media-composer-close" type="button" onClick={reset} aria-label="メディア追加を閉じる">
                  <X size={20} />
                </button>
              </div>
              <form className="media-inline-form" ref={mediaFormRef} onSubmit={save}>
                <h3>
                  {editingId
                    ? "メディアを編集"
                    : type === "image"
                      ? imageUploadMode === "bulk"
                        ? "写真を一括アップロード"
                        : "写真を追加"
                      : "YouTubeを追加"}
                </h3>
                <label>
                  種類
                  <select
                    value={type}
                    onChange={(event) => {
                      const nextType = event.target.value as MediaItem["type"];
                      setType(nextType);
                      if (nextType === "youtube") setImageUploadMode("single");
                    }}
                  >
                    <option value="youtube">YouTube</option>
                    <option value="image">写真アップロード</option>
                  </select>
                </label>
                <label>
                  タイトル
                  <input
                    ref={mediaTitleInputRef}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    required={type === "youtube"}
                  />
                </label>
                <label>
                  紐づける旅程
                  <select value={actionLabel} onChange={(event) => setActionLabel(event.target.value)}>
                    <option value="">未設定</option>
                    {selectedSections.map((section, index) => {
                      const action = parseItineraryAction(section);
                      return (
                        <option key={section.id} value={section.title}>
                          {action.dayLabel} {sectionTimeLabel(section, index)} {action.actionTitle}
                        </option>
                      );
                    })}
                  </select>
                </label>
                {type === "youtube" ? (
                  <label>
                    YouTube URL
                    <input ref={mediaUrlInputRef} value={url} onChange={(event) => setUrl(event.target.value)} required />
                  </label>
                ) : (
                  <label>
                    {imageUploadMode === "bulk" ? "写真ファイル（複数選択）" : "写真ファイル"}
                    <input
                      accept="image/*"
                      multiple={imageUploadMode === "bulk" && !editingId}
                      onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
                      required={!editingId}
                      ref={mediaFileInputRef}
                      type="file"
                    />
                    <small>
                      {files.length > 0
                        ? `${files.length}件を選択中`
                        : imageUploadMode === "bulk"
                          ? "複数の写真をまとめて選択できます。"
                          : "1枚ずつ写真を追加します。"}
                    </small>
                  </label>
                )}
                <label>
                  キャプション
                  <textarea value={caption} onChange={(event) => setCaption(event.target.value)} rows={3} />
                </label>
                <label>
                  表示順
                  <input value={sortOrder} onChange={(event) => setSortOrder(Number(event.target.value))} type="number" />
                </label>
                <div className="button-row">
                  <button className="primary" type="submit" disabled={uploading}>
                    {uploading ? "保存中..." : editingId ? "更新" : "追加"}
                  </button>
                  <button type="button" onClick={reset}>
                    閉じる
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}

        <div className="media-gallery-layout">
          <div className="media-card-grid">
            {mediaGroups.map((group) => (
              <section className="media-action-group" key={group.id}>
                <div className="media-action-group-head">
                  <div>
                    <span className="itinerary-list-meta">
                      {group.dayLabel && <b>{group.dayLabel}</b>}
                      {group.timeLabel && <b>{group.timeLabel}</b>}
                    </span>
                    <h3>{group.title}</h3>
                    {group.location && <small>{group.location}</small>}
                  </div>
                  <div className="media-action-group-tools">
                    <button type="button" onClick={() => startNewMedia("image", "bulk", group.actionLabel)}>
                      <Camera size={16} />
                      写真
                    </button>
                    <button type="button" onClick={() => startNewMedia("youtube", "single", group.actionLabel)}>
                      <Film size={16} />
                      動画
                    </button>
                  </div>
                </div>
                {group.items.length === 0 ? (
                  <div className="media-group-empty">この旅程にはまだ写真・動画がありません。</div>
                ) : (
                  <div className="media-action-items">
                    {group.items.map((item) => {
                      const itemCaption = splitMediaCaption(item.caption);
                      return (
                        <article className={selectedPreview?.id === item.id ? "media-card active" : "media-card"} key={item.id}>
                          <button className="media-card-open" type="button" onClick={() => setSelectedPreviewId(item.id)}>
                  <div className="media-card-thumb">
                    {item.type === "image" ? (
                      <CoverImage src={item.url} alt="" />
                    ) : (
                      <>
                        <Film size={28} />
                        <span>Video</span>
                      </>
                    )}
                    <span className="media-card-kind">{item.type === "image" ? <ImageIcon size={16} /> : <Film size={16} />}</span>
                  </div>
                  <strong>{item.title}</strong>
                  {itemCaption.actionLabel && <span className="memory-action-chip">{itemCaption.actionLabel}</span>}
                  <small>{itemCaption.caption || item.url}</small>
                          </button>
                  <span className="media-card-actions">
                    <button type="button" onClick={() => edit(item)}>
                      <Edit size={15} />
                      編集
                    </button>
                    <button type="button" onClick={() => remove(item)}>
                      <Trash2 size={15} />
                      削除
                    </button>
                  </span>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            ))}
          </div>

          <aside className="media-inspector">
            {selectedPreview ? (
              <>
                <div className="media-inspector-head">
                  <strong>選択中のメディア</strong>
                  <small>ここでは内容確認と既存メディアの編集開始だけを行います。</small>
                </div>
                <div className="media-inspector-preview">
                  {selectedPreview.type === "image" ? (
                    <CoverImage src={selectedPreview.url} alt="" />
                  ) : (
                    <iframe title={selectedPreview.title} src={youtubeEmbedUrl(selectedPreview.url)} />
                  )}
                </div>
                <h3>{selectedPreview.title}</h3>
                <div className="media-inspector-meta">
                  <span>{selectedPreview.type === "image" ? "写真" : "YouTube"}</span>
                  <span>表示順 {selectedPreview.sort_order}</span>
                </div>
                {selectedPreviewCaption.actionLabel && (
                  <span className="memory-action-chip">{selectedPreviewCaption.actionLabel}</span>
                )}
                <label>
                  キャプション
                  <textarea value={selectedPreviewCaption.caption} readOnly rows={3} />
                </label>
                <div className="button-row">
                  <button type="button" onClick={() => edit(selectedPreview)}>
                    <Edit size={18} />
                    編集
                  </button>
                  <button className="danger-outline" type="button" onClick={() => remove(selectedPreview)}>
                    <Trash2 size={18} />
                    削除
                  </button>
                </div>
              </>
            ) : (
              <EmptyState title="メディアがありません" body="写真またはYouTubeを追加してください。" />
            )}
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="editor-grid">
      <form className="form-grid" onSubmit={save}>
        {!lockedEventId && (
          <label>
            イベント
            <select
              value={eventId}
              onChange={(event) => {
                setEventId(event.target.value);
                setShowMediaAddPanel(false);
                setEditingId(null);
              }}
              required
            >
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          種類
          <select value={type} onChange={(event) => setType(event.target.value as MediaItem["type"])}>
            <option value="youtube">YouTube</option>
            <option value="image">写真アップロード</option>
          </select>
        </label>
        <label>
          タイトル
          <input value={title} onChange={(event) => setTitle(event.target.value)} required={type === "youtube"} />
          {type === "image" && <small>複数アップロード時は未入力ならファイル名をタイトルにします。</small>}
        </label>
        <label>
          紐づける旅程
          <select value={actionLabel} onChange={(event) => setActionLabel(event.target.value)}>
            <option value="">未設定</option>
            {selectedSections.map((section, index) => {
              const action = parseItineraryAction(section);
              return (
                <option key={section.id} value={section.title}>
                  {action.dayLabel} {sectionTimeLabel(section, index)} {action.actionTitle}
                </option>
              );
            })}
          </select>
        </label>
        {type === "youtube" ? (
          <label>
            YouTube URL
            <input value={url} onChange={(event) => setUrl(event.target.value)} required />
          </label>
        ) : (
          <label>
            写真ファイル
            <input
              accept="image/*"
              multiple={!editingId}
              onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
              required={!editingId}
              type="file"
            />
          </label>
        )}
        <label>
          キャプション
          <textarea value={caption} onChange={(event) => setCaption(event.target.value)} rows={3} />
        </label>
        <label>
          表示順
          <input value={sortOrder} onChange={(event) => setSortOrder(Number(event.target.value))} type="number" />
        </label>
        <div className="button-row">
          <button className="primary" type="submit" disabled={uploading}>
            {type === "image" ? <Upload size={18} /> : <Save size={18} />}
            {uploading ? "保存中..." : editingId ? "更新" : "追加"}
          </button>
          <button type="button" onClick={reset}>
            <Plus size={18} />
            新規
          </button>
        </div>
      </form>
      <div className="table-list">
        <div className="table-list-head">
          <h3>写真・動画一覧</h3>
          <button type="button" onClick={reset}>
            <Plus size={18} />
            新規メディア
          </button>
        </div>
        <div className="table-header media-table-header">
          <span>タイトル</span>
          <span>種類</span>
          <span>表示順</span>
          <span>操作</span>
        </div>
        {selectedMedia.map((item) => {
          const itemCaption = splitMediaCaption(item.caption);
          return (
            <div className="table-row media-table-row" key={item.id}>
              <span>
                <strong>{item.title}</strong>
                {itemCaption.actionLabel && <span className="memory-action-chip">{itemCaption.actionLabel}</span>}
                <small>{itemCaption.caption || item.url}</small>
              </span>
              <span className="media-kind">{item.type === "image" ? <ImageIcon size={18} /> : <Film size={18} />}</span>
              <span className="date-cell">{item.sort_order}</span>
              <button type="button" onClick={() => edit(item)} aria-label={`${item.title}を編集`}>
                <Edit size={18} />
              </button>
              <button type="button" onClick={() => remove(item)} aria-label={`${item.title}を削除`}>
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MembersEditor({
  allowedUsers,
  onRefresh,
  onMessage
}: {
  allowedUsers: AllowedUser[];
  onRefresh: () => Promise<void>;
  onMessage: (message: string | null) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AllowedUser["role"]>("viewer");
  const [saving, setSaving] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supabase) {
      onMessage("メンバー管理にはSupabase設定が必要です。");
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail.includes("@")) {
      onMessage("有効なメールアドレスを入力してください。");
      return;
    }

    setSaving(true);
    const { error } = await supabase.from("allowed_users").upsert({
      email: normalizedEmail,
      role
    });

    if (error) {
      setSaving(false);
      onMessage(`メンバー保存に失敗しました: ${error.message}`);
      return;
    }

    const profileResult = await supabase.from("profiles").update({ role }).eq("email", normalizedEmail);
    setSaving(false);
    if (profileResult.error) {
      onMessage(`許可メンバーは保存しましたが、既存プロフィール更新に失敗しました: ${profileResult.error.message}`);
      await onRefresh();
      return;
    }

    setEmail("");
    setRole("viewer");
    onMessage("許可メンバーを保存しました。");
    await onRefresh();
  };

  const edit = (user: AllowedUser) => {
    setEmail(user.email);
    setRole(user.role);
  };

  const remove = async (user: AllowedUser) => {
    if (!supabase) {
      onMessage("削除にはSupabase設定が必要です。");
      return;
    }
    if (user.email.toLowerCase() === ADMIN_EMAIL) {
      onMessage(`${ADMIN_EMAIL} は初期管理者のため削除できません。`);
      return;
    }
    if (!window.confirm(`${user.email} のログイン許可を削除します。`)) {
      return;
    }

    const { error } = await supabase.from("allowed_users").delete().eq("email", user.email);
    if (error) {
      onMessage(`メンバー削除に失敗しました: ${error.message}`);
      return;
    }

    const profileResult = await supabase.from("profiles").update({ role: "viewer" }).eq("email", user.email);
    if (profileResult.error) {
      onMessage(`許可メンバーは削除しましたが、既存プロフィール更新に失敗しました: ${profileResult.error.message}`);
      await onRefresh();
      return;
    }

    onMessage("許可メンバーを削除しました。");
    await onRefresh();
  };

  return (
    <div className="editor-grid">
      <form className="form-grid" onSubmit={save}>
        <label>
          Googleアカウントのメール
          <input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="family@example.com"
            type="email"
            required
          />
        </label>
        <label>
          権限
          <select value={role} onChange={(event) => setRole(event.target.value as AllowedUser["role"])}>
            <option value="viewer">viewer</option>
            <option value="admin">admin</option>
          </select>
        </label>
        <div className="button-row">
          <button className="primary" type="submit" disabled={saving}>
            <Save size={18} />
            {saving ? "保存中..." : "保存"}
          </button>
          <button
            type="button"
            onClick={() => {
              setEmail("");
              setRole("viewer");
            }}
          >
            <Plus size={18} />
            新規
          </button>
        </div>
      </form>
      <div className="table-list">
        <div className="table-list-head">
          <h3>許可メンバー</h3>
        </div>
        <div className="table-header members-table-header">
          <span>メール</span>
          <span>権限</span>
          <span>操作</span>
        </div>
        {allowedUsers.map((user) => (
          <div className="table-row members-table-row" key={user.email}>
            <span>
              <strong>{user.email}</strong>
              <small>{user.email.toLowerCase() === ADMIN_EMAIL ? "初期管理者" : "Googleログイン許可済み"}</small>
            </span>
            <span className={user.role === "admin" ? "badge live" : "badge"}>{user.role}</span>
            <button type="button" onClick={() => edit(user)} aria-label={`${user.email}を編集`}>
              <Edit size={18} />
            </button>
            <button type="button" onClick={() => remove(user)} aria-label={`${user.email}を削除`}>
              <Trash2 size={18} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignOutButton() {
  const signOut = async () => {
    await supabase?.auth.signOut();
  };

  return (
    <button type="button" onClick={signOut}>
      <LogOut size={18} />
      ログアウト
    </button>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <section className="empty-state">
      <Check size={28} />
      <h1>{title}</h1>
      <p>{body}</p>
    </section>
  );
}

export default App;
